import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged, signOut, sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, getDoc, Timestamp, updateDoc, arrayUnion, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { browserLocalPersistence, setPersistence } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from './firebase';
import { supabase } from './supabase';
import { 
  // Icons for navigation and UI
  Globe, Menu, X, User, Mail, Lock, Eye, EyeOff, LogOut, 
  ArrowRight, CheckCircle, Clock, Upload, FileText, CreditCard,
  Shield, ChevronRight, Camera, Loader2, Wallet,
  DollarSign, Briefcase, MapPin, Phone, Star, Award, Users,
  HardHat, Stethoscope, Coffee, Truck, Building, Download,
  ExternalLink, ChevronDown, Search, Filter, Heart, Share2,
  Facebook, Twitter, Instagram, Linkedin, Sun, Moon, Bell,
  Settings, HelpCircle, Info, MessageSquare, XCircle,
  Check, Home, Grid, List, ThumbsUp, TrendingUp,
  Compass, Navigation, Zap, Shield as ShieldIcon, Crown, Plane,
  GraduationCap, BookOpen, Calendar, Flag, FileCheck,
  Image, Paperclip, Link, Copy, QrCode, Quote, Send,
  Headphones, Bookmark, Video, Phone as PhoneIcon,
  MessageCircle, Smartphone, ShieldCheck, Eye as EyeIcon,
  ThumbsDown, ThumbsUp as ThumbsUpIcon,
  
  // 👇 ADD THESE NEW ICONS FOR ADMIN PANEL
  Trash2, AlertCircle, Eye as ViewIcon, ExternalLink as LinkIcon,
  CheckCircle as CheckCircleIcon, CreditCard as CreditCardIcon,
  FileText as FileIcon, Users as UsersIcon, Award as AwardIcon,
  RefreshCw, GraduationCap as GraduationIcon
} from 'lucide-react';



// --- COMPLETE TRANSLATION DICTIONARY (ALL LANGUAGES) ---
const DICTIONARY = {
  en: {
    dir: "ltr",
    font: "Inter, sans-serif",
    langCode: "en",
    labels: {
      brandName: "GlobalTravel",
      brandSub: "Enterprise",
      slogan: "Work Abroad. Pay Later.",
      currency: "USD",
      ethCurrency: "ETB",
      romCurrency: "RON",
      eurCurrency: "EUR",
      uaeCurrency: "AED",
      perMonth: "per month",
      days: "Days",
      hours: "Hours",
      minutes: "Minutes",
      loading: "Loading Systems...",
      error: "Error Occurred",
      success: "Action Successful",
      back: "Back",
      next: "Next",
      finish: "Finish",
      cancel: "Cancel",
      save: "Save Changes",
      required: "Required",
      optional: "Optional",
      all: "All",
      searchPlaceholder: "Search jobs or countries...",
      noResults: "No results found",
      viewMore: "View More Details"
    },
    nav: {
      home: "Home",
      jobs: "Job Vacancies",
      howItWorks: "Process Guide",
      portal: "Candidate Portal",
      admin: "Admin Panel",
      contact: "Contact Center"
    },
    hero: {
      title: "Your Global Career Starts Here.",
      sub: "We bridge the gap for skilled workers. We cover 80% of your initial travel and visa costs. You repay 30% from your future international salary.",
      ctaPrimary: "Apply for Credit Now",
      ctaSecondary: "Browse Vacancies",
      trustedBy: "Trusted by 1,500+ Professionals",
      guarantee: "100% Legal & Verified"
    },
    stats: {
      deployed: "1,200+ Deployed",
      deployedSub: "Successfully Working",
      countries: "12 Countries",
      countriesSub: "Global Partnerships",
      visaRate: "98% Success",
      visaRateSub: "Visa Approval Rate",
      offices: "4 Branches",
      officesSub: "Across Ethiopia"
    },
    jobs: {
      title: "Available Opportunities",
      sub: "Explore positions currently open for Ethiopian candidates under our credit migration model.",
      filterCountry: "Filter by Country",
      filterSector: "Filter by Sector",
      applyBtn: "Apply for this Role",
      salaryLabel: "Estimated Salary",
      locationLabel: "Work Location",
      typeLabel: "Employment Type",
      sectors: {
        construction: "Construction & Infrastructure",
        hospitality: "Hospitality & Tourism",
        logistics: "Logistics & Transport",
        healthcare: "Healthcare & Nursing",
        agriculture: "Modern Agriculture",
        manufacturing: "Industrial Manufacturing"
      },
      countries: {
        romania: "Romania",
        germany: "Germany",
        uae: "United Arab Emirates",
        qatar: "Qatar",
        saudi: "Saudi Arabia",
        poland: "Poland",
        lithuania: "Lithuania"
      },
      cities: {
        bucharest: "Bucharest",
        berlin: "Berlin",
        dubai: "Dubai",
        doha: "Doha",
        riyadh: "Riyadh",
        warsaw: "Warsaw",
        munich: "Munich",
        cluj: "Cluj-Napoca"
      },
      list: [
        { id: "j1", title: "General Construction Worker", sector: "construction", country: "romania", city: "bucharest", salary: "850", currency: "eurCurrency" },
        { id: "j2", title: "Professional Truck Driver (C+E)", sector: "logistics", country: "romania", city: "cluj", salary: "1200", currency: "eurCurrency" },
        { id: "j3", title: "Hotel Service Assistant", sector: "hospitality", country: "germany", city: "munich", salary: "1400", currency: "eurCurrency" },
        { id: "j4", title: "Warehouse Logistics Specialist", sector: "logistics", country: "uae", city: "dubai", salary: "3500", currency: "uaeCurrency" },
        { id: "j5", title: "Assistant Nurse", sector: "healthcare", country: "germany", city: "berlin", salary: "1800", currency: "eurCurrency" },
        { id: "j6", title: "Civil Engineer (Junior)", sector: "construction", country: "lithuania", city: "warsaw", salary: "1500", currency: "eurCurrency" }
      ]
    },
    steps: {
      title: "The 10-Step Journey",
      sub: "A transparent roadmap from Ethiopia to your new global destination.",
      durationLabel: "Expected Duration",
      reqLabel: "Requirements",
      costLabel: "Candidate Cost",
      items: [
        { id: 1, title: "Initial Consultation", desc: "Meet our counselors to discuss the credit model. We explain the 80/20 split and the 30% repayment structure.", icon: "MessageCircle", duration: "1-2 Hours", reqs: ["Valid ID", "Professional CV"], cost: "FREE" },
        { id: 2, title: "Registration & Fee", desc: "Official enrollment into the GlobalTravel database. Requires passport or National ID and photos.", icon: "DollarSign", duration: "Same Day", reqs: ["National ID/Passport", "2 Photos (3x4)"], cost: "$15 (Registration)" },
        { id: 3, title: "Acceptance Analysis", desc: "Our legal team reviews your background and issues an official Acceptance Letter if you qualify for credit.", icon: "CheckCircle", duration: "48 Hours", reqs: ["Background Check"], cost: "INCLUDED" },
        { id: 4, title: "Digital Verification", desc: "Setting up your digital profile and verifying your primary contact methods for international coordination.", icon: "Phone", duration: "24 Hours", reqs: ["Email Access", "Working Phone"], cost: "INCLUDED" },
        { id: 5, title: "Introduction Video", 
  desc: "Record a professional self-introduction for international employers. This is your digital interview.", 
  icon: "Video", 
  duration: "User Dependent", 
  reqs: ["Smart Attire", "Quiet Background", "Video Recording"], 
  cost: "INCLUDED",
  hasSubmit: true  // 👈 ADD THIS FLAG
},
        { id: 6, title: "Visa Application", desc: "Initiating the legal visa process with the respective embassy. We handle all legal paperwork and filing.", icon: "FileText", duration: "Processing Starts", reqs: ["Signed Affidavits", "Medical Clear"], cost: "$279 (Visa Fee)" },
        { id: 7, title: "Document Legalization", desc: "Legalizing educational certificates and civil status documents with relevant government bureaus.", icon: "Bookmark", duration: "7-10 Days", reqs: ["Grade 10/12 Certs", "Birth Certificate"], cost: "INCLUDED" },
        { id: 8, title: "Visa Approval", desc: "Notification of successful visa issuance. Received via Email and official SMS/WhatsApp channels.", icon: "AlertCircle", duration: "Embassy Dependent", reqs: ["Final Review"], cost: "INCLUDED" },
        { id: 9, title: "DHL Logistics", desc: "Secure shipping of your physical passport and visa documents to your chosen collection point.", icon: "Truck", duration: "48-72 Hours", reqs: ["Signature on Receipt"], cost: "$10 (Shipping)" },
        { id: 10, title: "Final Deployment", desc: "Orientation for your flight and arrival. Handover of the DHL receipt and travel itinerary.", icon: "Plane", duration: "Travel Day", reqs: ["Flight Ticket (Credit)"], cost: "READY" }
      ]
    },
    auth: {
      login: "Sign In",
      signup: "Join GlobalTravel",
      email: "Email Address",
      password: "Secure Password",
      forgot: "Forgot Password?",
      noAccount: "Don't have an account?",
      hasAccount: "Already a candidate?",
      google: "Continue with Google",
      welcomeBack: "Welcome Back, Candidate",
      createTitle: "Create Your Candidate Profile",
      creating: "Creating Account...",
      signingIn: "Signing in..."
    },
    portal: {
      welcome: "Welcome,",
      activeStatus: "Application Status: Active",
      overallProgress: "Deployment Progress",
      nextAction: "Your Next Action",
      documents: "My Documents",
      notifications: "Alerts & Updates",
      logout: "Log Out Securely",
      settings: "Profile Settings",
      verifyWait: "Awaiting Admin Verification",
      actionRequired: "Action Required Now",
      stepComplete: "Mark Step as Completed",
      history: "Activity History"
    },
    kyc: {
      title: "Basic KYC Information",
      step: "Step 1 of 4",
      fullName: "Full Name",
      dateOfBirth: "Date of Birth",
      gender: "Gender",
      male: "Male",
      female: "Female",
      other: "Other",
      nationality: "Nationality",
      phoneNumber: "Phone Number",
      address: "Address",
      city: "City",
      country: "Country",
      continue: "Continue to Education"
    },
    education: {
      title: "Education & Background",
      step: "Step 2 of 4",
      highestEducation: "Highest Education Level",
      fieldOfStudy: "Field of Study",
      institution: "Institution Name",
      graduationYear: "Graduation Year",
      grade: "Grade/GPA (Optional)",
      skills: "Skills (comma separated)",
      languages: "Languages (comma separated)",
      workExperience: "Work Experience",
      currentEmployment: "Current Employment Status",
      employed: "Employed Full-time",
      partTime: "Employed Part-time",
      selfEmployed: "Self-employed",
      unemployed: "Unemployed",
      student: "Student",
      continue: "Continue to Documents",
      levels: [
        "No Formal Education",
        "Primary School",
        "Secondary School",
        "High School Diploma",
        "Vocational Training",
        "Diploma",
        "Bachelor's Degree",
        "Master's Degree",
        "PhD",
        "Professional Certification"
      ]
    },
    documents: {
      title: "Upload Documents",
      step: "Step 3 of 4",
      uploaded: "Uploaded",
      uploading: "Uploading...",
      upload: "Upload",
      continue: "Continue to Payment",
      list: [
        { id: 'passport', name: 'Passport / ID Card', description: 'Clear photo of your passport or national ID' },
        { id: 'birthCertificate', name: 'Birth Certificate', description: 'Official birth certificate' },
        { id: 'photo', name: 'Passport Photo', description: 'Recent passport-sized photo' },
        { id: 'education', name: 'Education Certificate', description: 'Highest education certificate' },
        { id: 'work', name: 'Work Experience Letter', description: 'Letter from previous employer' },
        { id: 'cv', name: 'CV / Resume', description: 'Your updated CV' }
      ]
    },
    payment: {
      title: "Application Fee: $15 USD",
      step: "Step 4 of 4",
      usdt: "USDT (TRC20)",
      bank: "Bank Transfer (Ethiopia)",
      telebirr: "TeleBirr / M-Pesa",
      copy: "Copy",
      copied: "Copied!",
      uploadProof: "Upload Payment Proof",
      chooseFile: "Choose File",
      complete: "Complete Application",
      instructions: {
        usdt: ['Send $15 USDT (TRC20 network only)', 'Copy the address above', 'Send from your wallet', 'Take a screenshot of the transaction'],
        bank: ['Visit any Commercial Bank branch', 'Deposit $15 USD (or equivalent ETB)', 'Account Name: GlobalTravel PLC', 'Account Number: 1000123456789', 'Keep the deposit receipt'],
        telebirr: ['Open TeleBirr or M-Pesa', 'Send to: 251911223344', 'Amount: $15 USD (or equivalent ETB)', 'Reference: Your Full Name', 'Take a screenshot of the confirmation']
      }
    },
    testimonials: [
      { name: "Abraham Kasahun", role: "Construction in Romania", text: "I traveled with zero money. Today I earn €900/month. GlobalTravel changed my life.", location: "Bahir Dar / Bucharest" },
      { name: "Selamawit Tadesse", role: "Nurse in Germany", text: "The credit model is the only reason I could go to Europe. Truly professional service.", location: "Addis Ababa / Berlin" }
    ],
    footer: {
      desc: "The world's first credit-based labor migration agency, empowering Ethiopian talent for global markets.",
      links: "Quick Navigation",
      legal: "Legal Documents",
      contact: "Contact Information",
      rights: "All Rights Reserved",
      address: "Bole, Medhanialem Mall, 4th Floor, Addis Ababa, Ethiopia"
    }
  },
  am: {
    // Amharic translations (full version from your original code)
    dir: "ltr",
    font: "Noto Sans Ethiopic, sans-serif",
    langCode: "am",
    labels: {
      brandName: "ግሎባልትራቭል",
      brandSub: "ኢንተርፕራይዝ",
      slogan: "በውጭ አገር ይስሩ። በኋላ ይክፈሉ።",
      currency: "ዶላር",
      ethCurrency: "ብር",
      romCurrency: "ሮን",
      eurCurrency: "ዩሮ",
      uaeCurrency: "ዲርሃም",
      perMonth: "በወር",
      days: "ቀናት",
      hours: "ሰዓታት",
      minutes: "ደቂቃዎች",
      loading: "ሲስተሙ እየተጫነ ነው...",
      error: "ስህተት ተከስቷል",
      success: "ድርጊቱ ተሳክቷል",
      back: "ተመለስ",
      next: "ቀጣይ",
      finish: "ጨርስ",
      cancel: "ሰርዝ",
      save: "ለውጦችን አስቀምጥ",
      required: "ግዴታ",
      optional: "አማራጭ",
      all: "ሁሉም",
      searchPlaceholder: "ስራዎችን ወይም አገራትን ይፈልጉ...",
      noResults: "ምንም ውጤት አልተገኘም",
      viewMore: "ተጨማሪ ዝርዝሮችን ይመልከቱ"
    },
    nav: {
      home: "መነሻ",
      jobs: "ክፍት ስራዎች",
      howItWorks: "የሂደት መመሪያ",
      portal: "የአመልካች ፖርታል",
      admin: "የአስተዳዳሪ ፓነል",
      contact: "የመገናኛ ማዕከል"
    },
    hero: {
      title: "ዓለም አቀፍ የሥራ ሕይወትዎ እዚህ ይጀምራል።",
      sub: "ለሰለጠኑ ሠራተኞች ክፍተቱን እንሞላለን። 80% የሚሆነውን የመጀመሪያ የጉዞ እና የቪዛ ወጪዎን እንሸፍናለን። ቀሪውን 30% ከወደፊት ዓለም አቀፍ ደመወዝዎ ላይ እየተቀነሰ ይከፈላል።",
      ctaPrimary: "አሁኑኑ ለብድር ያመልክቱ",
      ctaSecondary: "ስራዎችን ይመልከቱ",
      trustedBy: "ከ1,500 በላይ ባለሙያዎች የታመነ",
      guarantee: "100% ህጋዊ እና የተረጋገጠ"
    },
    stats: {
      deployed: "1,200+ የተላኩ",
      deployedSub: "በተሳካ ሁኔታ እየሰሩ ያሉ",
      countries: "12 አገራት",
      countriesSub: "ዓለም አቀፍ አጋርነት",
      visaRate: "98% ስኬት",
      visaRateSub: "የቪዛ መጽደቅ መጠን",
      offices: "4 ቅርንጫፎች",
      officesSub: "በኢትዮጵያ ውስጥ"
    },
    jobs: {
      title: "ያሉ አማራጮች",
      sub: "በብድር ስደት ሞዴላችን ስር ለኢትዮጵያውያን አመልካቾች በአሁኑ ጊዜ ክፍት የሆኑ የሥራ ቦታዎችን ያስሱ።",
      filterCountry: "በአገር ይለዩ",
      filterSector: "በዘርፍ ይለዩ",
      applyBtn: "ለዚህ ስራ ያመልክቱ",
      salaryLabel: "የሚገመት ደመወዝ",
      locationLabel: "የስራ ቦታ",
      typeLabel: "የቅጥር ዓይነት",
      sectors: {
        construction: "ግንባታ እና መሠረተ ልማት",
        hospitality: "ሆቴል እና ቱሪዝም",
        logistics: "ሎጅስቲክስ እና ትራንስፖርት",
        healthcare: "ጤና ጥበቃ እና ነርሲንግ",
        agriculture: "ዘመናዊ ግብርና",
        manufacturing: "ኢንዱስትሪ ማኑፋክቸሪንግ"
      },
      countries: {
        romania: "ሮማንያ",
        germany: "ጀርመን",
        uae: "የተባበሩት አረብ ኤምሬትስ",
        qatar: "ኳታር",
        saudi: "ሳውዲ አረቢያ",
        poland: "ፖላንድ",
        lithuania: "ሊቱዌኒያ"
      },
      cities: {
        bucharest: "ቡካሬስት",
        berlin: "በርሊን",
        dubai: "ዱባይ",
        doha: "ዶሃ",
        riyadh: "ሪያድ",
        warsaw: "ዋርሶ",
        munich: "ሙኒክ",
        cluj: "ክሉጅ-ናፖካ"
      },
      list: [
        { id: "j1", title: "አጠቃላይ የግንባታ ሰራተኛ", sector: "construction", country: "romania", city: "bucharest", salary: "850", currency: "eurCurrency" },
        { id: "j2", title: "ፕሮፌሽናል የጭነት መኪና አሽከርካሪ (C+E)", sector: "logistics", country: "romania", city: "cluj", salary: "1200", currency: "eurCurrency" },
        { id: "j3", title: "የሆቴል አገልግሎት ረዳት", sector: "hospitality", country: "germany", city: "munich", salary: "1400", currency: "eurCurrency" },
        { id: "j4", title: "የመጋዘን ሎጅስቲክስ ባለሙያ", sector: "logistics", country: "uae", city: "dubai", salary: "3500", currency: "uaeCurrency" },
        { id: "j5", title: "ረዳት ነርስ", sector: "healthcare", country: "germany", city: "berlin", salary: "1800", currency: "eurCurrency" },
        { id: "j6", title: "ሲቪል መሃንዲስ (ጀማሪ)", sector: "construction", country: "lithuania", city: "warsaw", salary: "1500", currency: "eurCurrency" }
      ]
    },
    steps: {
      title: "የ10 ደረጃዎች ጉዞ",
      sub: "ከኢትዮጵያ ወደ አዲሱ ዓለም አቀፍ መዳረሻዎ ግልጽ የሆነ የሂደት ካርታ።",
      durationLabel: "የሚፈጅበት ጊዜ",
      reqLabel: "መስፈርቶች",
      costLabel: "የአመልካች ወጪ",
      items: [
        { id: 1, title: "የመጀመሪያ ምክክር", desc: "ስለ ብድር ሞዴሉ ለመወያየት ከአማካሪዎቻችን ጋር ይገናኙ። ስለ 80/20 ክፍፍል እና ስለ 30% ክፍያ አወቃቀር እናስረዳለን።", icon: "MessageCircle", duration: "1-2 ሰዓታት", reqs: ["የሚሰራ መታወቂያ", "የስራ ልምድ (CV)"], cost: "ነፃ" },
        { id: 2, title: "ምዝገባ እና ክፍያ", desc: "በግሎባልትራቭል ዳታቤዝ ውስጥ ይፋዊ ምዝገባ። ፓስፖርት ወይም ብሔራዊ መታወቂያ እና ፎቶዎችን ይፈልጋል።", icon: "DollarSign", duration: "በዕለቱ", reqs: ["መታወቂያ/ፓስፖርት", "2 ፎቶዎች (3x4)"], cost: "15 ዶላር (ምዝገባ)" },
        { id: 3, title: "የተቀባይነት ትንተና", desc: "የህግ ቡድናችን የእርስዎን ታሪክ ይገመግማል እና ለብድር ብቁ ከሆኑ ይፋዊ የተቀባይነት ደብዳቤ ይሰጣል።", icon: "CheckCircle", duration: "48 ሰዓታት", reqs: ["የታሪክ ማጣራት"], cost: "የተካተተ" },
        { id: 4, title: "ዲጂታል ማረጋገጫ", desc: "የዲጂታል መገለጫዎን ማዋቀር እና ለዓለም አቀፍ ቅንጅት የመገናኛ ዘዴዎችዎን ማረጋገጥ።", icon: "Phone", duration: "24 ሰዓታት", reqs: ["የኢሜል አድራሻ", "የሚሰራ ስልክ"], cost: "የተካተተ" },
        { id: 5, title: "የመግቢያ ቪዲዮ", desc: "ለዓለም አቀፍ ቀጣሪዎች ፕሮፌሽናል ራስን የማስተዋወቂያ ቪዲዮ ይቅረጹ። ይህ የእርስዎ ዲጂታል ቃለ መጠይቅ ነው።", icon: "Video", duration: "በአመልካቹ ላይ የተመሰረተ", reqs: ["ሥርዓታማ አለባበስ", "ጸጥ ያለ ቦታ"], cost: "የተካተተ" },
        { id: 6, title: "የቪዛ ማመልከቻ", desc: "ከሚመለከተው ኤምባሲ ጋር ህጋዊ የቪዛ ሂደትን መጀመር። ሁሉንም የህግ ወረቀቶች እና ፋይሎችን እኛ እናከናውናለን።", icon: "FileText", duration: "ሂደቱ ተጀምሯል", reqs: ["የተፈረሙ ሰነዶች", "የጤና ምርመራ"], cost: "279 ዶላር (የቪዛ ክፍያ)" },
        { id: 7, title: "የሰነዶች ህጋዊነት", desc: "የትምህርት ማስረጃዎችን እና የሲቪል ሁኔታ ሰነዶችን ከሚመለከታቸው የመንግስት ቢሮዎች ጋር ህጋዊ ማድረግ።", icon: "Bookmark", duration: "7-10 ቀናት", reqs: ["የ10/12ኛ ክፍል ማስረጃ", "የልደት የምስክር ወረቀት"], cost: "የተካተተ" },
        { id: 8, title: "የቪዛ መጽደቅ", desc: "የቪዛ ስኬታማነት ማሳወቂያ። በኢሜል እና በይፋዊ ኤስኤምኤስ/ዋትስአፕ ቻናሎች ይደርስዎታል።", icon: "AlertCircle", duration: "በኤምባሲው ይወሰናል", reqs: ["የመጨረሻ ግምገማ"], cost: "የተካተተ" },
        { id: 9, title: "የዲኤችኤል (DHL) ሎጂስቲክስ", desc: "የእርስዎን ፓስፖርት እና የቪዛ ሰነዶች በመረጡት የመሰብሰቢያ ቦታ ደህንነቱ በተጠበቀ ሁኔታ ማጓጓዝ።", icon: "Truck", duration: "48-72 ሰዓታት", reqs: ["ርክክብ ላይ መፈረም"], cost: "10 ዶላር (ማጓጓዣ)" },
        { id: 10, title: "የመጨረሻ ስምሪት", desc: "ለበረራዎ እና ለመድረሻዎ ዝግጅት። የDHL ደረሰኝ እና የጉዞ መርሃ ግብር ርክክብ።", icon: "Plane", duration: "የጉዞ ቀን", reqs: ["የበረራ ትኬት (ብድር)"], cost: "ዝግጁ" }
      ]
    },
    auth: {
      login: "ይግቡ",
      signup: "ግሎባልትራቭልን ይቀላቀሉ",
      email: "የኢሜል አድራሻ",
      password: "ደህንነቱ የተጠበቀ የይለፍ ቃል",
      forgot: "የይለፍ ቃል ረሱ?",
      noAccount: "መለያ የለዎትም?",
      hasAccount: "አመልካች ነዎት?",
      google: "በጎግል ይቀጥሉ",
      welcomeBack: "እንኳን ደህና መጡ፣ አመልካች",
      createTitle: "የአመልካች መገለጫዎን ይፍጠሩ",
      creating: "መለያ እየተፈጠረ ነው...",
      signingIn: "በመግባት ላይ..."
    },
    portal: {
      welcome: "እንኳን ደህና መጡ፣",
      activeStatus: "የማመልከቻ ሁኔታ፡ ንቁ",
      overallProgress: "የጉዞ ሂደት",
      nextAction: "ቀጣይ ተግባርዎ",
      documents: "የእኔ ሰነዶች",
      notifications: "ማሳወቂያዎች",
      logout: "ደህንነቱ በተጠበቀ ሁኔታ ይውጡ",
      settings: "የመገለጫ ቅንብሮች",
      verifyWait: "የአስተዳዳሪ ማረጋገጫ በመጠባበቅ ላይ",
      actionRequired: "አሁን እርምጃ ያስፈልጋል",
      stepComplete: "ደረጃውን እንደተጠናቀቀ ያረጋግጡ",
      history: "የእንቅስቃሴ ታሪክ"
    },
    kyc: {
      title: "መሰረታዊ የKYC መረጃ",
      step: "ደረጃ 1 ከ 4",
      fullName: "ሙሉ ስም",
      dateOfBirth: "የትውልድ ቀን",
      gender: "ጾታ",
      male: "ወንድ",
      female: "ሴት",
      other: "ሌላ",
      nationality: "ዜግነት",
      phoneNumber: "ስልክ ቁጥር",
      address: "አድራሻ",
      city: "ከተማ",
      country: "አገር",
      continue: "ወደ ትምህርት ይቀጥሉ"
    },
    education: {
      title: "ትምህርት እና ዳራ",
      step: "ደረጃ 2 ከ 4",
      highestEducation: "ከፍተኛ የትምህርት ደረጃ",
      fieldOfStudy: "የትምህርት መስክ",
      institution: "የተቋም ስም",
      graduationYear: "የምረቃ ዓመት",
      grade: "ውጤት/GPA (አማራጭ)",
      skills: "ክህሎቶች (በነጠላ ሰረዝ የተለዩ)",
      languages: "ቋንቋዎች (በነጠላ ሰረዝ የተለዩ)",
      workExperience: "የስራ ልምድ",
      currentEmployment: "አሁን ያለው የስራ ሁኔታ",
      employed: "የሙሉ ጊዜ ተቀጣሪ",
      partTime: "የትርፍ ጊዜ ተቀጣሪ",
      selfEmployed: "የግል ሥራ",
      unemployed: "ሥራ አጥ",
      student: "ተማሪ",
      continue: "ወደ ሰነዶች ይቀጥሉ",
      levels: [
        "መደበኛ ትምህርት የለም",
        "አንደኛ ደረጃ ትምህርት ቤት",
        "ሁለተኛ ደረጃ ትምህርት ቤት",
        "ሁለተኛ ደረጃ ዲፕሎማ",
        "የሙያ ሥልጠና",
        "ዲፕሎማ",
        "የመጀመሪያ ዲግሪ",
        "የሁለተኛ ዲግሪ",
        "ዶክትሬት",
        "የሙያ ሰርተፍኬት"
      ]
    },
    documents: {
      title: "ሰነዶችን ይስቀሉ",
      step: "ደረጃ 3 ከ 4",
      uploaded: "ተስቅሏል",
      uploading: "በመስቀል ላይ...",
      upload: "ስቀል",
      continue: "ወደ ክፍያ ይቀጥሉ",
      list: [
        { id: 'passport', name: 'ፓስፖርት / መታወቂያ', description: 'የፓስፖርትዎ ወይም የብሔራዊ መታወቂያዎ ግልጽ ፎቶ' },
        { id: 'birthCertificate', name: 'የልደት የምስክር ወረቀት', description: 'ኦፊሴላዊ የልደት የምስክር ወረቀት' },
        { id: 'photo', name: 'የፓስፖርት ፎቶ', description: 'የቅርብ ጊዜ የፓስፖርት መጠን ፎቶ' },
        { id: 'education', name: 'የትምህርት የምስክር ወረቀት', description: 'ከፍተኛው የትምህርት የምስክር ወረቀት' },
        { id: 'work', name: 'የሥራ ልምድ ደብዳቤ', description: 'ከቀድሞ አሠሪ የተገኘ ደብዳቤ' },
        { id: 'cv', name: 'CV / ሪዙሜ', description: 'የዘመነው CVዎ' }
      ]
    },
    payment: {
      title: "የማመልከቻ ክፍያ: $15 ዶላር",
      step: "ደረጃ 4 ከ 4",
      usdt: "USDT (TRC20)",
      bank: "የባንክ ዝውውር (ኢትዮጵያ)",
      telebirr: "ቴሌብር / ኤም-ፔሳ",
      copy: "ቅዳ",
      copied: "ተቀድቷል!",
      uploadProof: "የክፍያ ማረጋገጫ ይስቀሉ",
      chooseFile: "ፋይል ይምረጡ",
      complete: "ማመልከቻውን ያጠናቅቁ",
      instructions: {
        usdt: ['$15 USDT ይላኩ (TRC20 አውታረ መረብ ብቻ)', 'ከላይ ያለውን አድራሻ ቅዳ', 'ከኪስ ቦርሳዎ ይላኩ', 'የግብይቱን ቅጽበታዊ ገጽ እይታ ያንሱ'],
        bank: ['ማንኛውንም የንግድ ባንክ ቅርንጫፍ ይጎብኙ', '$15 ዶላር (ወይም ተመጣጣኝ ብር) ይክፈሉ', 'የመለያ ስም: GlobalTravel PLC', 'የመለያ ቁጥር: 1000123456789', 'የተቀማጭ ደረሰኙን ያስቀምጡ'],
        telebirr: ['ቴሌብር ወይም ኤም-ፔሳ ይክፈቱ', 'ወደ: 251911223344 ይላኩ', 'መጠን: $15 ዶላር (ወይም ተመጣጣኝ ብር)', 'ማጣቀሻ: የእርስዎ ሙሉ ስም', 'የማረጋገጫውን ቅጽበታዊ ገጽ እይታ ያንሱ']
      }
    },
    testimonials: [
      { name: "አብርሃም ካሳሁን", role: "ግንባታ በሮማንያ", text: "ያለ ምንም ገንዘብ ተጓዝኩ። ዛሬ በወር 900 ዩሮ አገኛለሁ። ግሎባልትራቭል ህይወቴን ቀይሮታል።", location: "ባህር ዳር / ቡካሬስት" },
      { name: "ሰላማዊት ታደሰ", role: "ነርስ በጀርመን", text: "ወደ አውሮፓ መሄድ የቻልኩት በብድር ሞዴሉ ምክንያት ብቻ ነው። በእውነት ፕሮፌሽናል አገልግሎት።", location: "አዲስ አበባ / በርሊን" }
    ],
    footer: {
      desc: "ለኢትዮጵያ ተሰጥኦዎች ዓለም አቀፍ ገበያን ተደራሽ ለማድረግ የተቋቋመ የዓለም የመጀመሪያው በብድር ላይ የተመሠረተ የሠራተኛ ስምሪት ኤጀንሲ።",
      links: "ፈጣን አገናኞች",
      legal: "ህጋዊ ሰነዶች",
      contact: "የመገናኛ መረጃ",
      rights: "መብቱ በህግ የተጠበቀ ነው",
      address: "ቦሌ፣ መድኃኒዓለም ሞል፣ 4ኛ ፎቅ፣ አዲስ አበባ፣ ኢትዮጵያ"
    }
  },
  om: {
    // Oromo translations (full version from your original code)
    dir: "ltr",
    font: "Inter, sans-serif",
    langCode: "om",
    labels: {
      brandName: "GlobalTravel",
      brandSub: "Enterprise",
      slogan: "Biyya Alaa Hojjedhu. Booda Kafali.",
      currency: "Dollar",
      ethCurrency: "Birrii",
      romCurrency: "RON",
      eurCurrency: "EUR",
      uaeCurrency: "AED",
      perMonth: "ji'atti",
      days: "Guyyoota",
      hours: "Sa'aatii",
      minutes: "Daqiiqaa",
      loading: "Sirni fe'amaa jira...",
      error: "Dogoggorri uumamee jira",
      success: "Gochi milkaa'ee jira",
      back: "Deebi'i",
      next: "Itti aana",
      finish: "Xumuri",
      cancel: "Haqi",
      save: "Jijjiirama Ol-kaayi",
      required: "Dirqama",
      optional: "Filannoo",
      all: "Hunda",
      searchPlaceholder: "Hojii ykn biyya barbaadi...",
      noResults: "Bu'aan hin argamne",
      viewMore: "Bal'ina ilaali"
    },
    nav: {
      home: "Mana",
      jobs: "Hojiiwwan Banaman",
      howItWorks: "Qajeelfama Adeemsaa",
      portal: "Portal Kaadhimamaa",
      admin: "Admin Panel",
      contact: "Wiirtuu Quunnamtii"
    },
    hero: {
      title: "Hojiin Addunyaa Keessan Asitti Jalqaba.",
      sub: "Hojjettoota dandeettii qabaniif kaffaltii dursaa ni uffisna. 80% baasii imalaa fi viizaa keessan ni dandama. Mindaa keessan irraa ji'atti 30% nuuf deebiftu.",
      ctaPrimary: "Liqiif Amma Galmeessi",
      ctaSecondary: "Hojiiwwan Ilaali",
      trustedBy: "Ogeessota 1,500+ olitti amanamaa",
      guarantee: "100% Seera-qabeessa & Mirkanaa'aa"
    },
    stats: {
      deployed: "1,200+ Kan Ergaman",
      deployedSub: "Milkaa'inaan Hojjechaa Jiru",
      countries: "Biyyoota 12",
      countriesSub: "Waliigaltee Addunyaa",
      visaRate: "98% Milkaa'ina",
      visaRateSub: "Mirkaneessa Viizaa",
      offices: "Dammoota 4",
      officesSub: "Itoophiyaa Keessatti"
    },
    jobs: {
      title: "Carraawwan Jiran",
      sub: "Hojiiwwan iyyattoota Itoophiyaaf model liqii keenyaan banaman asitti ilaali.",
      filterCountry: "Biyyaan Filter Godhi",
      filterSector: "Sekteraan Filter Godhi",
      applyBtn: "Hojii kanaaf iyyadhu",
      salaryLabel: "Tilmaama Mindaa",
      locationLabel: "Bakka Hojii",
      typeLabel: "Gosa Qaxaraa",
      sectors: {
        construction: "Ijaarsaa fi Infrasitiraakcherii",
        hospitality: "Hoteelaa fi Turiizimii",
        logistics: "Loojistiiksii fi Traanispoortii",
        healthcare: "Tajaajila Fayyaa",
        agriculture: "Qonna Ammayyaa",
        manufacturing: "Indastirii"
      },
      countries: {
        romania: "Roomaaniyaa",
        germany: "Jarman",
        uae: "Emireetota Arabaa",
        qatar: "Qaxaar",
        saudi: "Sa'udii Arabiyaa",
        poland: "Pooland",
        lithuania: "Lituwaaniyaa"
      },
      cities: {
        bucharest: "Buukaarest",
        berlin: "Berlin",
        dubai: "Dubayi",
        doha: "Doohaa",
        riyadh: "Riyaad",
        warsaw: "Waarsoo",
        munich: "Muuniik",
        cluj: "Kuluuj-Naapookaa"
      },
      list: [
        { id: "j1", title: "Hojjetaa Ijaarsaa Waliigalaa", sector: "construction", country: "romania", city: "bucharest", salary: "850", currency: "eurCurrency" },
        { id: "j2", title: "Konkolaachisaa Galmee (C+E)", sector: "logistics", country: "romania", city: "cluj", salary: "1200", currency: "eurCurrency" },
        { id: "j3", title: "Gargaaraa Hoteelaa", sector: "hospitality", country: "germany", city: "munich", salary: "1400", currency: "eurCurrency" },
        { id: "j4", title: "Ogeessa Loojistiiksii", sector: "logistics", country: "uae", city: "dubai", salary: "3500", currency: "uaeCurrency" },
        { id: "j5", title: "Gargaaraa Narshii", sector: "healthcare", country: "germany", city: "berlin", salary: "1800", currency: "eurCurrency" },
        { id: "j6", title: "Injiinera Siivilii", sector: "construction", country: "lithuania", city: "warsaw", salary: "1500", currency: "eurCurrency" }
      ]
    },
    steps: {
      title: "Imala Sadarkaa 10",
      sub: "Itoophiyaa irraa hanga biyya alaa deemtutti adeemsa ifa ta'e.",
      durationLabel: "Yeroo fudhatu",
      reqLabel: "Ulaagaalee",
      costLabel: "Baasii Keessan",
      items: [
        { id: 1, title: "Maree Jalqabaa", desc: "Gorsa keenya waliin waa'ee model liqii mari'adhu. Akkaataa itti 80/20 kaffalamu ni ibsina.", icon: "MessageCircle", duration: "Sa'aatii 1-2", reqs: ["Waraqaa Eenyummaa", "CV"], cost: "BILISA" },
        { id: 2, title: "Galmee fi Kaffaltii", desc: "Galmee seeraa GlobalTravel. Paaspoortii ykn waraqaa eenyummaa fi suuraa gaafata.", icon: "DollarSign", duration: "Guyyuma Sana", reqs: ["Paaspoortii", "Suuraa 2 (3x4)"], cost: "$15 (Galmee)" },
        { id: 3, title: "Xiinxala Fudhatamaa", desc: "Garee seeraa keenyaan ragaan keessan erga ilaalamee booda xalayaa fudhatamaa isiniif kennama.", icon: "CheckCircle", duration: "Sa'aatii 48", reqs: ["Mirkaneessa Ragaa"], cost: "UFFIFAMEERA" },
        { id: 4, title: "Mirkaneessa Diijitaalaa", desc: "Profile diijitaalaa qopheessuu fi quunnamtii keessan mirkaneessuu.", icon: "Phone", duration: "Sa'aatii 24", reqs: ["Imeeyilii", "Bilbila"], cost: "UFFIFAMEERA" },
        { id: 5, title: "Viidiyoo Of-beeksisaa", desc: "Hojjechiiftota biyya alaaf viidiyoo of-beeksisaa qopheessuu. Kun qormaata keessan isa jalqabaati.", icon: "Video", duration: "Harkuma Keessan", reqs: ["Uffata Sirrii", "Bakka Tasgabbaa'aa"], cost: "UFFIFAMEERA" },
        { id: 6, title: "Galmee Viizaa", desc: "Imbaasii keessatti adeemsa viizaa seeraa jalqabuu. Ragaalee hunda nuti xumurra.", icon: "FileText", duration: "Adeemsi Jalqabeera", reqs: ["Waraqaa Mallatteeffame", "Fayyaa"], cost: "$279 (Viizaa)" },
        { id: 7, title: "Mirkaneessa Ragaalee", desc: "Ragaalee barnootaa fi waraqaa dhalootaa biirolee mootummaa irraa mirkaneessisuu.", icon: "Bookmark", duration: "Guyyaa 7-10", reqs: ["Ragaa 10/12", "Waraqaa Dhalootaa"], cost: "UFFIFAMEERA" },
        { id: 8, title: "Mirkanaa'uu Viizaa", desc: "Viizaan keessan mirkanaa'uu isaa Imeeyilii fi WhatsApp'n isiniif ergama.", icon: "AlertCircle", duration: "Imbaasii irratti hundaa'a", reqs: ["Xiinxala Dhumaa"], cost: "UFFIFAMEERA" },
        { id: 9, title: "Loojistiiksii DHL", desc: "Paaspoortii fi viizaa keessan karaa DHL nagaadhaan isin ga'uu.", icon: "Truck", duration: "Sa'aatii 48-72", reqs: ["Mallattoo Nagahee"], cost: "$10 (DHL)" },
        { id: 10, title: "Deployment Dhumaa", desc: "Qophii imalaa fi qajeelfama balali'insa xiyyaaraa. Nagaan deemaa!", icon: "Plane", duration: "Guyyaa Imalaa", reqs: ["Tiikeettii (Liqii)"], cost: "QOPHII" }
      ]
    },
    auth: {
      login: "Seeni",
      signup: "GlobalTravel Itti Makami",
      email: "Imeeyilii",
      password: "Password Nageenyaa",
      forgot: "Password dagattee?",
      noAccount: "Account hin qabduu?",
      hasAccount: "Iyyataa duraanii?",
      google: "Google'n itti fufi",
      welcomeBack: "Baga Nagaan Dhufte",
      createTitle: "Profile kee banadhu",
      creating: "Banamaa jira...",
      signingIn: "Seenamaa jira..."
    },
    portal: {
      welcome: "Baga Nagaan Dhufte,",
      activeStatus: "Haala Iyyannoo: Hojii irra",
      overallProgress: "Adeemsa Imalaa",
      nextAction: "Hojii Itti Aanu",
      documents: "Dokumentii koo",
      notifications: "Beeksisoota",
      logout: "Ba'i",
      settings: "Sirreeffama Keessaa",
      verifyWait: "Mirkaneessa eeggachaa jiru",
      actionRequired: "Gocha ammaa",
      stepComplete: "Mirkaneessi",
      history: "Hordoffii"
    },
    kyc: {
      title: "Odeeffannoo KYC Bu'uraa",
      step: "Sadarkaa 1 keessaa 4",
      fullName: "Maqaa Guutuu",
      dateOfBirth: "Guyyaa Dhalootaa",
      gender: "Saala",
      male: "Dhiira",
      female: "Dubara",
      other: "Kan biroo",
      nationality: "Lammummaa",
      phoneNumber: "Lakkoofsa Bilbilaa",
      address: "Teessoo",
      city: "Magaalaa",
      country: "Biyya",
      continue: "Barnootatti Itti Fufi"
    },
    education: {
      title: "Barnootaa fi Duub-dhaaba",
      step: "Sadarkaa 2 keessaa 4",
      highestEducation: "Sadarkaa Barnootaa Olaanaa",
      fieldOfStudy: "Qo'annoo",
      institution: "Maqaa Dhaabbataa",
      graduationYear: "Bara Xumuraa",
      grade: "Sadarkaa (Filannoo)",
      skills: "Dandeettii (kommaan addaan baasi)",
      languages: "Afaanota (kommaan addaan baasi)",
      workExperience: "Muuxannoo Hojii",
      currentEmployment: "Haala Hojii Ammaa",
      employed: "Hojjetaa Yeroo Guutuu",
      partTime: "Hojjetaa Yeroo Gartokkee",
      selfEmployed: "Hojii Dhuunfaa",
      unemployed: "Hojii maleessa",
      student: "Barataa",
      continue: "Dokumentiitti Itti Fufi",
      levels: [
        "Barnoota Addaa Hin qabne",
        "Mana Barumsaa Sadarkaa 1",
        "Mana Barumsaa Sadarkaa 2",
        "Dippilooma Sadarkaa 2",
        "Leenjii Ogguummaa",
        "Dippilooma",
        "Digrii Jalqabaa",
        "Digrii Lammaffaa",
        "PhD",
        "Ragassa Ogguummaa"
      ]
    },
    documents: {
      title: "Dokumentiiwwan Fe'i",
      step: "Sadarkaa 3 keessaa 4",
      uploaded: "Fe'ameera",
      uploading: "Fe'aa jira...",
      upload: "Fe'i",
      continue: "Kaffaltiitti Itti Fufi",
      list: [
        { id: 'passport', name: 'Paaspoortii / Eenyummaa', description: 'Suuraa ifa ta\'e paaspoortii ykn eenyummaa' },
        { id: 'birthCertificate', name: 'Waraqaa Dhalootaa', description: 'Waraqaa dhalootaa seeraa' },
        { id: 'photo', name: 'Suuraa Paaspoortii', description: 'Suuraa haaraa paaspoortii' },
        { id: 'education', name: 'Ragaa Barnootaa', description: 'Ragaa barnootaa olaanaa' },
        { id: 'work', name: 'Xalayaa Muuxannoo Hojii', description: 'Xalayaa abbootii hojii duraanii irraa' },
        { id: 'cv', name: 'CV / Seenaa Jireenyaa', description: 'CV keessan kan haarame' }
      ]
    },
    payment: {
      title: "Kaffaltii Iyyannoo: $15 USD",
      step: "Sadarkaa 4 keessaa 4",
      usdt: "USDT (TRC20)",
      bank: "Dabarsa Baankii (Itoophiyaa)",
      telebirr: "TeleBirr / M-Pesa",
      copy: "Kuqi",
      copied: "Kuqame!",
      uploadProof: "Ragaa Kaffaltii Fe'i",
      chooseFile: "Faayilii Filadhu",
      complete: "Iyyannoo Xumuri",
      instructions: {
        usdt: ['$15 USDT ergi (TRC20 qofa)', 'Teessoo olii kuqi', 'Waalii keessan irraa ergi', 'Suuraa dalagaa kaasi'],
        bank: ['Damee Baankii kamiiyyuu daawwadhu', '$15 USD ykn qixa Birrii kaffali', 'Maqaa: GlobalTravel PLC', 'Lakkoofsa: 1000123456789', 'Risaatii eegi'],
        telebirr: ['TeleBirr ykn M-Pesa banadhu', 'Ergi: 251911223344', 'Qixa: $15 USD ykn qixa Birrii', 'Yaadanno: Maqaa Guutuu Kee', 'Suuraa mirkaneessaa kaasi']
      }
    },
    testimonials: [
      { name: "Abraham Kasahun", role: "Ijaarsa Roomaaniyaa", text: "Maallaqa malee imale. Har'a ji'atti €900 argadha. GlobalTravel jireenya koo jijjiirre.", location: "Bahir Dar / Bucharest" },
      { name: "Selamawit Tadesse", role: "Narsii Jarman", text: "Model liqii sababa isaa qofaan gara Awurooppa deemuu danda'e. Tajaajila ogummaa.", location: "Finfinnee / Berlin" }
    ],
    footer: {
      desc: "Dhaabbata godaansa hojjettootaa liqii irratti hundaa'e kan addunyaa isa jalqabaa, Itoophiyaa dandeettii olaanaa godhu.",
      links: "Qajeelfama",
      legal: "Seeraa fi Qajeelfama",
      contact: "Odeeffannoo Quunnamtii",
      rights: "Mirgi hunduu eegamaadha",
      address: "Bole, Medhanialem Mall, 4th Floor, Finfinnee, Itoophiyaa"
    }
  }
};

// Force Firebase to keep user logged in even after refresh
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log('Auth persistence set to LOCAL');
  })
  .catch((error) => {
    console.error('Auth persistence error:', error);
  });

// Send admin email function
const sendAdminEmail = async (type, data) => {
  try {
    const formData = new FormData();
    formData.append('_subject', `GlobalTravel: ${type}`);
    formData.append('_captcha', 'false');
    
    let message = '';
    if (type === 'new_user') message = `🚀 NEW USER SIGNED UP!\n\nName: ${data.name}\nEmail: ${data.email}\nUser ID: ${data.userId}\nTime: ${new Date().toLocaleString()}\n\nLogin: https://globaltravelagency.vercel.app/admin`;
    else if (type === 'payment_upload') message = `💰 NEW PAYMENT UPLOADED!\n\nUser: ${data.userName}\nEmail: ${data.userEmail}\nAmount: $${data.amount}\nPayment ID: ${data.paymentId}\nTime: ${new Date().toLocaleString()}`;
    else if (type === 'document_upload') message = `📄 DOCUMENT UPLOADED!\n\nUser: ${data.userName}\nEmail: ${data.userEmail}\nDocument: ${data.documentType}\nTime: ${new Date().toLocaleString()}`;
    else if (type === 'application_submitted') message = `📋 APPLICATION SUBMITTED!\n\nUser: ${data.userName}\nEmail: ${data.userEmail}\nDocuments: ${data.documentsCount}/6\nPayment Status: ${data.paymentStatus}\nTime: ${new Date().toLocaleString()}`;
    
    formData.append('message', message);
    await fetch('https://formsubmit.co/ajax/melkajapi@gmail.com', { method: 'POST', body: formData });
    console.log(`✅ Email sent: ${type}`);
  } catch (error) {
    console.error('Email error:', error);
  }
};

const IconMap = { MessageCircle, DollarSign, CheckCircle, PhoneIcon, Video, FileText, Bookmark, AlertCircle, Truck, Plane };

export default function App() {
  const [lang, setLang] = useState('en');
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [view, setView] = useState('home');
  const [loading, setLoading] = useState(true);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [applicationStep, setApplicationStep] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const t = useMemo(() => DICTIONARY[lang], [lang]);
  
  useEffect(() => {
    console.log('Supabase client initialized:', supabase);
  }, []);
  
  useEffect(() => {
  let isMounted = true;
  
  const unsubscribe = onAuthStateChanged(auth, async (u) => {
    console.log('Auth state changed:', u ? u.email : 'No user');
    
    if (!isMounted) return;
    
    if (u && !u.isAnonymous) {
      try {
        // 🔥 CRITICAL: Always refresh token when auth state changes
        await u.getIdToken(true);
        await u.reload();
        
        console.log('User verified status after refresh:', u.emailVerified);
        
        if (isMounted) {
          setUser(u);
          
          // Check if admin
          if (u.email === 'admin@globaltravel.com') {
            setIsAdmin(true);
          } else {
            // Check verification status
            if (!u.emailVerified) {
              console.log('User not verified, signing out');
              await signOut(auth);
              showNotification('Please verify your email before logging in. Check your inbox!', 'info');
              setUser(null);
            } else {
              // Load user data from Firestore
              const userDoc = doc(db, 'users', u.uid);
              const docSnap = await getDoc(userDoc);
              if (docSnap.exists()) {
                setUserData(docSnap.data());
                if (docSnap.data().applicationStep) {
                  setApplicationStep(docSnap.data().applicationStep);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Auth error:', error);
        if (isMounted) {
          await signOut(auth);
          setUser(null);
          setIsAdmin(false);
          setUserData(null);
        }
      }
    } else {
      if (isMounted) {
        console.log('No user signed in');
        setUser(null);
        setIsAdmin(false);
        setUserData(null);
      }
    }
    
    if (isMounted) {
      setLoading(false);
    }
  });
  
  return () => {
    isMounted = false;
    unsubscribe();
  };
}, []);

  useEffect(() => {
    // Check if we just came from verification page
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    
    if (verified === 'success' && auth.currentUser) {
      const refreshUser = async () => {
        try {
          await auth.currentUser.getIdToken(true);
          await auth.currentUser.reload();
          console.log('User refreshed after verification');
          showNotification('Email verified! You can now log in.', 'success');
        } catch (error) {
          console.error('Error refreshing user:', error);
        }
      };
      refreshUser();
    }
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type }), 5000);
  };

  const navigate = (v, job = null) => {
    if (job) setSelectedJob(job);
    setView(v);
    setMobileMenu(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <LoadingScreen t={t} />;

  return (
    <div style={{ fontFamily: t.font }} className="min-h-screen bg-slate-950 text-slate-100">
      {notification.show && (
        <div className={`fixed top-24 right-4 z-50 max-w-sm bg-slate-900 border ${
          notification.type === 'success' ? 'border-emerald-500/50' : 
          notification.type === 'info' ? 'border-blue-500/50' : 'border-amber-500/50'
        } rounded-xl p-4`}>
          <p className="text-sm">{notification.message}</p>
          <button onClick={() => setNotification({ show: false, message: '', type: '' })} className="absolute top-2 right-2 text-slate-500">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Navigation - FIXED for mobile */}
      <nav className="fixed top-0 w-full z-[100] px-2 sm:px-4 py-2 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between backdrop-blur-2xl bg-slate-900/60 border border-white/5 rounded-xl sm:rounded-2xl lg:rounded-3xl p-2 sm:p-3 lg:px-6 shadow-2xl">
          <div onClick={() => navigate('home')} className="flex items-center gap-2 sm:gap-3 cursor-pointer shrink-0">
            <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-1.5 sm:p-2 rounded-lg sm:rounded-xl text-slate-950 shadow-lg">
              <Globe size={18} className="sm:w-[22px] sm:h-[22px] lg:w-7 lg:h-7" strokeWidth={3}/>
            </div>
            <div className="flex flex-col">
              <span className="text-sm sm:text-lg lg:text-xl font-black tracking-tighter leading-none">{t.labels.brandName}</span>
              <span className="text-[6px] sm:text-[8px] uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold text-amber-500">{t.labels.brandSub}</span>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {['home', 'jobs', 'howItWorks', 'contact'].map(k => (
              <button key={k} onClick={() => navigate(k)} className={`text-[10px] xl:text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${view === k ? 'text-amber-500' : 'text-slate-400 hover:text-white'}`}>
                {t.nav[k]}
              </button>
            ))}
            {isAdmin && (
              <button onClick={() => navigate('admin')} className={`text-[10px] xl:text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${view === 'admin' ? 'text-amber-500' : 'text-slate-400 hover:text-white'}`}>
                {t.nav.admin}
              </button>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 lg:gap-3">
            <select 
              value={lang} 
              onChange={(e) => setLang(e.target.value)}
              className="bg-white/5 border border-white/10 text-slate-300 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-bold text-[8px] sm:text-[10px] outline-none hover:border-amber-500/30 transition-all cursor-pointer"
            >
              <option value="en">EN</option>
              <option value="am">አማ</option>
              <option value="om">OM</option>
            </select>
            
            {user && !user.isAnonymous ? (
              <>
                {isAdmin ? (
                  <button onClick={() => navigate('admin')} className="bg-amber-500 text-slate-950 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-black text-[8px] sm:text-[10px] uppercase tracking-widest shadow-lg hover:scale-105 transition-all whitespace-nowrap">
                    Admin
                  </button>
                ) : (
                  <button onClick={() => navigate('portal')} className="bg-amber-500 text-slate-950 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-black text-[8px] sm:text-[10px] uppercase tracking-widest shadow-lg hover:scale-105 transition-all whitespace-nowrap">
                    {t.nav.portal}
                  </button>
                )}
                <button 
  onClick={async () => {
    await signOut(auth);
    navigate('home');           // ← add this line
    showNotification('Logged out successfully', 'success');
  }}
  className="p-1.5 sm:p-2 bg-white/5 rounded-lg sm:rounded-xl hover:bg-white/10"
>
  <LogOut size={14} className="sm:w-4 sm:h-4 lg:w-[18px] lg:h-[18px]" />
</button>
              </>
            ) : (
              <button onClick={() => navigate('auth')} className="bg-white text-slate-950 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-black text-[8px] sm:text-[10px] uppercase tracking-widest hover:bg-amber-500 transition-all whitespace-nowrap">
                {t.auth.login}
              </button>
            )}

            <button onClick={() => setMobileMenu(!mobileMenu)} className="lg:hidden p-1.5 sm:p-2 bg-white/5 rounded-lg sm:rounded-xl">
              {mobileMenu ? <X size={16} className="sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" /> : <Menu size={16} className="sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Nav */}
      {mobileMenu && (
        <div className="fixed inset-0 z-[90] bg-slate-950/95 backdrop-blur-3xl pt-16 sm:pt-20 px-4 sm:px-6 overflow-y-auto">
          <div className="flex flex-col gap-4 sm:gap-6 max-w-sm mx-auto">
            {Object.keys(t.nav).map(k => (
              <button key={k} onClick={() => navigate(k)} className="text-2xl sm:text-3xl font-black tracking-tighter text-left hover:text-amber-500 border-b border-white/5 pb-3 sm:pb-4 transition-all">
                {t.nav[k]}
              </button>
            ))}
            <div className="mt-8 sm:mt-12 pt-4 sm:pt-6 border-t border-white/10 space-y-3 sm:space-y-4">
              <div className="text-slate-500 text-xs font-bold">{t.footer.contact}</div>
              <div className="text-white font-black text-sm sm:text-base">+251 911 00 00 00</div>
            </div>
          </div>
        </div>
      )}

      <main className="pt-20 sm:pt-24 pb-16 px-4 max-w-7xl mx-auto">
        {view === 'home' && <HomeView t={t} navigate={navigate} />}
        {view === 'jobs' && <JobsView t={t} navigate={navigate} showNotification={showNotification} />}
        {view === 'howItWorks' && <HowItWorksView t={t} />}
        {view === 'auth' && <AuthView t={t} navigate={navigate} showNotification={showNotification} setIsAdmin={setIsAdmin} />}
        {view === 'portal' && <PortalView t={t} user={user} userData={userData} navigate={navigate} showNotification={showNotification} applicationStep={applicationStep} setApplicationStep={setApplicationStep} />}
        {view === 'admin' && <AdminView t={t} user={user} navigate={navigate} showNotification={showNotification} />}
        {view === 'contact' && <ContactView t={t} showNotification={showNotification} />}
        {view === 'kyc' && <KYCView t={t} user={user} navigate={navigate} showNotification={showNotification} setApplicationStep={setApplicationStep} />}
        {view === 'education' && <EducationView t={t} user={user} navigate={navigate} showNotification={showNotification} setApplicationStep={setApplicationStep} />}
        {view === 'documents' && <DocumentsView t={t} user={user} navigate={navigate} showNotification={showNotification} setApplicationStep={setApplicationStep} />}
        {view === 'payment' && <PaymentView t={t} user={user} navigate={navigate} showNotification={showNotification} setApplicationStep={setApplicationStep} />}
      </main>
    </div>
  );
}

// ============= HOME VIEW =============
function HomeView({ t, navigate }) {
  return (
    <div className="space-y-32">
      <section className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center min-h-[60vh] sm:min-h-[70vh]">
        <div className="space-y-6 sm:space-y-8 lg:space-y-10">
          <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 sm:py-2 bg-amber-500/10 border border-amber-500/20 rounded-full">
            <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-amber-500 rounded-full animate-ping" />
            <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-amber-500">{t.labels.slogan}</span>
          </div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl xl:text-[92px] font-black tracking-tighter leading-[0.9] text-white">
            {t.hero.title}
          </h1>
          <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-slate-400 font-medium leading-relaxed max-w-lg">
            {t.hero.sub}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
            <button onClick={() => navigate('auth')} className="bg-amber-500 text-slate-950 px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest hover:scale-105 transition-all shadow-xl shadow-amber-500/20 w-full sm:w-auto">
              {t.hero.ctaPrimary}
            </button>
            <button onClick={() => navigate('jobs')} className="bg-white/5 border border-white/10 px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest hover:bg-white/10 transition-all w-full sm:w-auto">
              {t.hero.ctaSecondary}
            </button>
          </div>
          <div className="pt-4 sm:pt-6 lg:pt-8 border-t border-white/5 flex gap-6 sm:gap-8 lg:gap-12">
            <div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-black">{t.stats.deployed}</div>
              <div className="text-[8px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-widest">{t.stats.deployedSub}</div>
            </div>
            <div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-black">{t.stats.visaRate}</div>
              <div className="text-[8px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-widest">{t.stats.visaRateSub}</div>
            </div>
          </div>
        </div>
        <div className="relative group perspective-1000 hidden lg:block">
          <div className="bg-slate-900 rounded-[3rem] p-3 border border-white/5 transform transition-all duration-700 hover:rotate-2 shadow-3xl">
            <img 
              src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=800" 
              className="w-full h-full object-cover rounded-[2.5rem] opacity-70"
              alt="Candidate"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
            <div className="absolute bottom-12 left-12 right-12 space-y-4">
              <Quote size={24} className="text-amber-500" />
              <p className="text-2xl font-black tracking-tight italic">"{t.testimonials[0].text}"</p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-800 border border-white/10 overflow-hidden">
                  <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${t.testimonials[0].name}`} alt="Avatar" />
                </div>
                <div>
                  <div className="font-black text-sm">{t.testimonials[0].name}</div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{t.testimonials[0].role}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900/40 py-32 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-4 mb-24">
            <h2 className="text-5xl md:text-6xl font-black tracking-tighter">{t.steps.title}</h2>
            <p className="text-slate-400 font-medium max-w-xl mx-auto">{t.steps.sub}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {t.steps.items.slice(0, 5).map((s, i) => (
              <StepMiniCard key={i} step={s} t={t} />
            ))}
          </div>
          <div className="mt-16 text-center">
            <button onClick={() => navigate('howItWorks')} className="group inline-flex items-center gap-3 text-amber-500 font-black text-[11px] uppercase tracking-[0.3em]">
              {t.labels.viewMore}
              <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform"/>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function StepMiniCard({ step, t }) {
  const Icon = IconMap[step.icon] || Info;
  return (
    <div className="group bg-slate-950 border border-white/5 p-8 rounded-[2rem] hover:border-amber-500/30 transition-all hover:-translate-y-2">
      <div className="text-4xl font-black text-slate-900 group-hover:text-amber-500/10 transition-colors mb-6">0{step.id}</div>
      <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center text-amber-500 mb-6 group-hover:scale-110 transition-transform">
        <Icon size={24}/>
      </div>
      <h3 className="text-lg font-black mb-3">{step.title}</h3>
      <p className="text-[11px] text-slate-500 font-bold leading-relaxed">{step.desc.substring(0, 80)}...</p>
    </div>
  );
}

// ============= JOBS VIEW =============
function JobsView({ t, navigate, showNotification }) {
  const [sector, setSector] = useState('All');
  const [country, setCountry] = useState('All');

  const filtered = t.jobs.list.filter(j => 
    (sector === 'All' || j.sector === sector) && 
    (country === 'All' || j.country === country)
  );

  const handleApply = (job) => {
    if (!auth.currentUser) {
      navigate('auth', job);
      showNotification('Please sign in to apply for jobs', 'info');
    } else {
      navigate('portal');
      showNotification('Start your application from the dashboard', 'info');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 space-y-16">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <div className="space-y-4">
          <h2 className="text-6xl font-black tracking-tighter">{t.jobs.title}</h2>
          <p className="text-slate-400 font-medium max-w-xl">{t.jobs.sub}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <FilterDropdown label={t.jobs.filterSector} value={sector} onChange={setSector} options={['All', ...Object.keys(t.jobs.sectors)]} t={t} isSector />
          <FilterDropdown label={t.jobs.filterCountry} value={country} onChange={setCountry} options={['All', ...Object.keys(t.jobs.countries)]} t={t} isCountry />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filtered.map(j => (
          <JobCard key={j.id} job={j} t={t} handleApply={handleApply} />
        ))}
      </div>
    </div>
  );
}

function FilterDropdown({ label, value, onChange, options, t, isSector }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-2">{label}</span>
      <select 
        value={value} 
        onChange={e => onChange(e.target.value)}
        className="bg-slate-900 border border-white/10 text-white px-5 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest outline-none focus:border-amber-500/50 cursor-pointer"
      >
        {options.map(opt => (
          <option key={opt} value={opt}>
            {opt === 'All' ? t.labels.all : (isSector ? t.jobs.sectors[opt] : t.jobs.countries[opt])}
          </option>
        ))}
      </select>
    </div>
  );
}

function JobCard({ job, t, handleApply }) {
  const Icon = job.sector === 'construction' ? HardHat : job.sector === 'logistics' ? Truck : job.sector === 'healthcare' ? Stethoscope : Coffee;
  
  return (
    <div className="group bg-slate-900 border border-white/5 rounded-[2.5rem] p-10 hover:border-amber-500/30 transition-all">
      <div className="flex justify-between items-start mb-10">
        <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-amber-500 group-hover:scale-110 transition-transform duration-500">
          <Icon size={32}/>
        </div>
        <div className="px-4 py-2 bg-emerald-500/10 text-emerald-500 text-[10px] font-black uppercase tracking-widest rounded-full border border-emerald-500/20">
          {t.jobs.typeLabel}
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-500/70 mb-2">
            {t.jobs.sectors[job.sector]}
          </div>
          <h3 className="text-2xl font-black tracking-tight">{job.title}</h3>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/5 p-4 rounded-2xl">
            <div className="text-[8px] font-black uppercase tracking-widest text-slate-500 mb-1">{t.jobs.locationLabel}</div>
            <div className="text-xs font-black flex items-center gap-2">
              <MapPin size={12} className="text-amber-500"/>
              {t.jobs.cities[job.city]}, {t.jobs.countries[job.country]}
            </div>
          </div>
          <div className="bg-white/5 p-4 rounded-2xl">
            <div className="text-[8px] font-black uppercase tracking-widest text-slate-500 mb-1">{t.jobs.salaryLabel}</div>
            <div className="text-xs font-black flex items-center gap-2">
              <DollarSign size={12} className="text-emerald-500"/>
              {job.salary} {t.labels[job.currency]} / {t.labels.perMonth}
            </div>
          </div>
        </div>
        <button onClick={() => handleApply(job)} className="w-full bg-white text-slate-950 py-5 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-amber-500 transition-all shadow-xl">
          {t.jobs.applyBtn}
        </button>
      </div>
    </div>
  );
}

// ============= HOW IT WORKS VIEW =============
function HowItWorksView({ t }) {
  return (
    <div className="max-w-5xl mx-auto px-6 space-y-24">
      <div className="text-center space-y-6">
        <h2 className="text-7xl font-black tracking-tighter">{t.steps.title}</h2>
        <p className="text-slate-400 font-medium max-w-2xl mx-auto text-lg leading-relaxed">{t.steps.sub}</p>
      </div>
      <div className="relative space-y-12">
        <div className="absolute left-[39px] top-10 bottom-10 w-0.5 bg-white/5 -z-10" />
        {t.steps.items.map((s, i) => {
          const IconComp = IconMap[s.icon] || Info;
          return (
            <div key={i} className="flex gap-10 group">
              <div className="flex-shrink-0 w-20 h-20 bg-slate-950 border border-white/5 rounded-full flex items-center justify-center font-black text-2xl group-hover:border-amber-500/40 group-hover:text-amber-500 transition-all shadow-xl">
                {s.id}
              </div>
              <div className="flex-grow bg-slate-900/50 border border-white/5 p-10 rounded-[2.5rem] hover:border-amber-500/20 transition-all space-y-8">
                <div className="flex flex-col md:flex-row justify-between gap-6">
                  <div className="flex items-center gap-6">
                    <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500">
                      <IconComp size={32} />
                    </div>
                    <div>
                      <h3 className="text-3xl font-black tracking-tight">{s.title}</h3>
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">{t.steps.durationLabel}: {s.duration}</div>
                    </div>
                  </div>
                  <div className="bg-amber-500 text-slate-950 px-6 py-3 rounded-2xl h-fit text-center">
                    <div className="text-[8px] font-black uppercase tracking-widest opacity-70 mb-1">{t.steps.costLabel}</div>
                    <div className="text-xs font-black">{s.cost}</div>
                  </div>
                </div>
                <p className="text-lg text-slate-400 font-medium leading-relaxed">{s.desc}</p>
                <div className="flex flex-wrap gap-4">
                  {s.reqs.map((r, j) => (
                    <div key={j} className="flex items-center gap-3 bg-white/5 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-300">
                      <Check size={14} className="text-amber-500"/> {r}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============= AUTH VIEW =============
function AuthView({ t, navigate, showNotification, setIsAdmin }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // SIMPLE URL CHECK - NO FIREBASE MAGIC
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    
    if (verified === 'success') {
      showNotification('✅ Email verified! You can now log in.', 'success');
      // Clean the URL
      window.history.replaceState({}, document.title, '/auth');
    }
  }, []);

  const handleForgotPassword = async () => {
    if (!email) {
      showNotification('Please enter your email address', 'error');
      return;
    }
    
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email, {
        url: 'https://globaltravelagency.vercel.app/auth',
        handleCodeInApp: true
      });
      setResetSent(true);
      showNotification('Password reset email sent! Check your inbox.', 'success');
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  try {
    if (mode === 'signup') {
      // SIGN UP
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send verification email
      await sendEmailVerification(userCredential.user);
      
      // Save user data to Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        name: name || email.split('@')[0],
        email: email,
        createdAt: Timestamp.now(),
        applicationStep: 1,
        status: 'pending',
        documents: {},
        payments: [],
        kyc: null,
        education: null
      });

      // Sign out immediately (they need to verify email first)
      await signOut(auth);
      
      // Show success message
      setResetSent(true);
      showNotification('Verification email sent! Please check your inbox.', 'success');
      
    } else if (mode === 'login') {
      // LOGIN
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // 🔥 CRITICAL: Force refresh the token to get latest verification status
      await userCredential.user.getIdToken(true);
      await userCredential.user.reload();
      
      console.log('User verified status after refresh:', userCredential.user.emailVerified);
      
      // Check if admin
      if (userCredential.user.email === 'admin@globaltravel.com') {
        setIsAdmin(true);
        navigate('admin');
      } else if (!userCredential.user.emailVerified) {
        await signOut(auth);
        showNotification('Please verify your email first. Check your inbox!', 'info');
      } else {
        showNotification('Login successful!', 'success');
        navigate('portal');
      }
    }
  } catch (error) {
    showNotification(error.message, 'error');
  } finally {
    setLoading(false);
  }
};

  if (resetSent) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-slate-900 p-8 rounded-2xl text-center">
          <Mail size={48} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Check Your Email</h2>
          <p className="text-slate-400 mb-6">
            We've sent a verification/reset link to <span className="text-amber-500">{email}</span>
          </p>
          <button
            onClick={() => { setResetSent(false); setMode('login'); }}
            className="bg-amber-500 text-slate-950 px-6 py-3 rounded-xl font-bold"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-slate-900 p-8 rounded-2xl">
        <h2 className="text-3xl font-bold text-center mb-6">
          {mode === 'login' ? 'Welcome Back' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
        </h2>

        {mode === 'forgot' ? (
          <div className="space-y-4">
            <p className="text-slate-400 text-sm mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.auth.email}
              className="w-full bg-slate-950 p-4 rounded-xl"
            />
            <button
              onClick={handleForgotPassword}
              disabled={loading}
              className="w-full bg-amber-500 text-slate-950 p-4 rounded-xl font-bold"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <button
              onClick={() => setMode('login')}
              className="w-full text-center text-sm text-slate-400 hover:text-amber-500"
            >
              ← Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full Name"
                className="w-full bg-slate-950 p-4 rounded-xl"
                required
              />
            )}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.auth.email}
              className="w-full bg-slate-950 p-4 rounded-xl"
              required
            />
            {mode !== 'forgot' && (
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.auth.password}
                  className="w-full bg-slate-950 p-4 rounded-xl pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-4 top-4 text-slate-400"
                >
                  {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            )}

            {mode === 'login' && (
              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="text-sm text-amber-500 hover:underline float-right"
              >
                {t.auth.forgot}
              </button>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 text-slate-950 p-4 rounded-xl font-bold"
            >
              {loading ? 'Loading...' : mode === 'login' ? t.auth.login : t.auth.signup}
            </button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                className="text-sm text-slate-400 hover:text-amber-500"
              >
                {mode === 'login' ? t.auth.noAccount : t.auth.hasAccount}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============= PORTAL VIEW WITH MULTI-PAGE DASHBOARD =============
function PortalView({ t, user, userData, navigate, showNotification, applicationStep, setApplicationStep }) {
  const [currentPage, setCurrentPage] = useState('overview');
  const [videoSubmitted, setVideoSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [visaAppointmentDate, setVisaAppointmentDate] = useState('');
  const [visaPaymentProof, setVisaPaymentProof] = useState(null);
  const [visaUploading, setVisaUploading] = useState(false);
  const [visaSubmitted, setVisaSubmitted] = useState(userData?.visa?.status === 'pending' || false);
  const [showWorkMilestone, setShowWorkMilestone] = useState(false);
  const [showVisaMilestone, setShowVisaMilestone] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('usdt'); // default to USDT
  const [paymentProof, setPaymentProof] = useState(null);
              
    useEffect(() => {
  // Work Milestone: show right after payment proof uploaded (even if pending)
  if (
    (videoSubmitted || applicationStep > 5) && 
    userData?.payment?.proof  // just check if proof exists
  ) {
    setShowWorkMilestone(true);
  }

  // Visa Milestone: show right after visa proof uploaded
  if (visaPaymentProof) {
    setShowVisaMilestone(true);
  }
}, [userData, videoSubmitted, applicationStep, visaPaymentProof]);
              
  const currentStep = userData?.applicationStep || 1;
  const progress = (currentStep / 10) * 100;

  const handleVideoSubmit = async () => {
    setUploading(true);
    // Simulate video upload
    setTimeout(() => {
      setVideoSubmitted(true);
      setUploading(false);
      showNotification('Introduction video submitted successfully!', 'success');
      
      // Update application step if needed
      if (currentStep === 5) {
        const newStep = currentStep + 1;
        setApplicationStep(newStep);
        // Update in Firebase
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, { applicationStep: newStep });
      }
    }, 2000);
  };
              // Add this function inside PortalView (after all useState lines)
const handleFileUpload = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*,.pdf';
  input.style.display = 'none';

  input.onchange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true); // assuming you have setUploading state

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64Data = reader.result;

      setPaymentProof(base64Data);
      setUploading(false);
      showNotification('Payment proof uploaded! Admin will review.', 'success');

      // Save to Firestore (your existing logic - adjust if needed)
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        payment: {
          method: paymentMethod,
          amount: 15,
          proof: base64Data,
          status: 'pending',
          submittedAt: Timestamp.now()
        },
        applicationStep: 5,
        status: 'under-review',
        updatedAt: Timestamp.now()
      });

      // Optional: trigger milestone after upload
      setShowWorkMilestone(true);
    };
  };

  document.body.appendChild(input);
  input.click();
  document.body.removeChild(input);
};

  const handleStartApplication = () => {
    if (!userData) return;
    
    if (!userData.kyc) {
      navigate('kyc');
    } else if (!userData.education) {
      navigate('education');
    } else if (!userData.documents || Object.keys(userData.documents).length < 6) {
      navigate('documents');
    } else if (!userData.payment) {
      navigate('payment');
    }
  };

  const pages = [
    { id: 'overview', name: 'Overview', icon: Home },
    { id: 'kyc', name: 'KYC Information', icon: User, step: 1, completed: !!userData?.kyc },
    { id: 'education', name: 'Education', icon: GraduationCap, step: 2, completed: !!userData?.education },
    { id: 'documents', name: 'Documents', icon: FileText, step: 3, completed: userData?.documents && Object.keys(userData.documents).length >= 6 },
    { id: 'payment', name: 'Payment', icon: CreditCard, step: 4, completed: !!userData?.payment },
    { id: 'video', name: 'Introduction Video', icon: Video, step: 5, completed: videoSubmitted },
    { id: 'visa', name: 'Visa Processing', icon: Plane, step: 6, locked: currentStep < 6 },
    { id: 'deployment', name: 'Deployment', icon: Briefcase, step: 10, locked: currentStep < 10 }
  ];

  const renderPage = () => {
    switch(currentPage) {
case 'visa':
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-10">

      {/* MILESTONE 1: 3–7 days after video + app fee */}
      {(videoSubmitted || applicationStep > 5) &&
       userData?.payment?.status === 'approved' &&
       userData?.visa?.status !== 'pending' ? (
        <div className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-gradient-to-br from-emerald-950 via-slate-950 to-emerald-950 border border-emerald-500/40 rounded-3xl max-w-3xl w-full p-10 md:p-16 text-center relative shadow-2xl shadow-emerald-900/30">
            <button
              onClick={() => setShowWorkMilestone(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-emerald-400 transition-colors"
            >
              <X size={32} />
            </button>

            <div className="w-24 h-24 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-10">
              <CheckCircle size={64} className="text-emerald-500 animate-pulse" />
            </div>

            <h2 className="text-4xl md:text-5xl font-black mb-8 text-emerald-400 tracking-tight">
              Next Stage Started!
            </h2>

            <div className="space-y-8 text-slate-200 text-xl md:text-2xl leading-relaxed max-w-2xl mx-auto">
              <p>Your documents and application fee have been approved. We have now started the next phase.</p>

              <div className="bg-emerald-900/30 border border-emerald-500/30 p-10 rounded-2xl">
                <h3 className="text-3xl font-bold text-emerald-300 mb-6">Estimated Time</h3>
                <p className="text-5xl font-black text-white mb-8">3–7 Working Days</p>
                <p className="text-2xl font-semibold mb-6">for:</p>
                <ul className="space-y-6 text-left text-lg">
                  <li className="flex items-start gap-5">
                    <CheckCircle size={32} className="text-emerald-400 shrink-0 mt-1" />
                    <span>Work Acceptance Letter from employer</span>
                  </li>
                  <li className="flex items-start gap-5">
                    <CheckCircle size={32} className="text-emerald-400 shrink-0 mt-1" />
                    <span>Work Permit / Visa issuance</span>
                  </li>
                </ul>
              </div>

              <p className="text-xl md:text-2xl font-semibold text-amber-300 mt-10">
                After receiving both documents, you can apply for the visa at the embassy.
              </p>

              <p className="text-slate-400 mt-8">
                We will notify you immediately via email and in the portal as soon as both documents are ready.
              </p>
            </div>

            <button
              onClick={() => {
                setShowWorkMilestone(false);
                setCurrentPage('overview');
              }}
              className="mt-12 bg-emerald-600 hover:bg-emerald-500 text-white px-12 py-6 rounded-2xl font-bold text-xl transition-all shadow-xl shadow-emerald-900/40"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      ) : userData?.visa?.status === 'pending' ? (
        /* MILESTONE 2: 1–4 months after visa fee */
        <div className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-gradient-to-br from-blue-950 via-slate-950 to-blue-950 border border-blue-500/40 rounded-3xl max-w-3xl w-full p-10 md:p-16 text-center relative shadow-2xl shadow-blue-900/30">
            <button
              onClick={() => setShowVisaMilestone(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-blue-400 transition-colors"
            >
              <X size={32} />
            </button>

            <div className="w-24 h-24 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-10">
              <Loader2 size={64} className="text-blue-400 animate-spin" />
            </div>

            <h2 className="text-4xl md:text-5xl font-black mb-8 text-blue-400 tracking-tight">
              Visa Application In Progress
            </h2>

            <div className="space-y-8 text-slate-200 text-xl md:text-2xl leading-relaxed max-w-2xl mx-auto">
              <p className="text-lg md:text-xl">
                Your $3,500 visa processing fee has been received and is now under review.
              </p>

              <div className="bg-blue-900/30 border border-blue-500/30 p-10 rounded-2xl">
                <h3 className="text-3xl font-bold text-blue-300 mb-6">Current Status</h3>
                <p className="text-2xl font-semibold mb-6">
                  Your visa application is currently undergoing inspection by the country
                </p>
                <p className="text-5xl font-black text-white mb-8">
                  1–4 Months
                </p>
              </div>

              <p className="text-slate-400">
                We will notify you immediately once there is an update.
              </p>
            </div>

            <button
              onClick={() => {
                setShowVisaMilestone(false);
                setCurrentPage('overview');
              }}
              className="mt-12 bg-blue-600 hover:bg-blue-500 text-white px-12 py-6 rounded-2xl font-bold text-xl transition-all shadow-xl shadow-blue-900/40"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      ) : (
        /* NORMAL VISA PAGE — all your original features */
        <div className="space-y-10">
          <h3 className="text-3xl md:text-4xl font-bold text-center">Visa Processing – Step 6</h3>

          {/* Visa Overview */}
          <div className="bg-slate-900 border border-white/10 p-8 md:p-10 rounded-3xl">
            <h4 className="text-2xl md:text-3xl font-bold mb-6 text-amber-400">Visa Processing Overview</h4>
            <div className="space-y-6 text-slate-300 text-base md:text-lg">
              <p>
                After your documents are approved and the application fee is paid, we immediately start the official visa process with the embassy of the destination country.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                  <h5 className="font-bold text-amber-400 mb-3 text-xl">Estimated Time</h5>
                  <p className="text-lg">3–7 working days for:</p>
                  <ul className="list-disc list-inside mt-3 space-y-2 text-slate-400">
                    <li>Work Acceptance Letter from employer</li>
                    <li>Work Permit / Visa issuance</li>
                  </ul>
                </div>
                <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                  <h5 className="font-bold text-amber-400 mb-3 text-xl">Visa Processing Fee</h5>
                  <p className="text-3xl md:text-4xl font-black text-emerald-400">$3,500 USD</p>
                  <p className="text-slate-400 mt-2 text-base">
                    (Covers embassy fees, legal coordination, document handling, and priority processing)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Preferred Appointment Date */}
          <div className="bg-slate-900 border border-white/10 p-8 md:p-10 rounded-3xl">
            <h4 className="text-2xl font-bold mb-4">Preferred Embassy Appointment Date</h4>
            <p className="text-slate-400 mb-6">
              Choose a date or range when you are available for the embassy interview. We will try to schedule within your preference.
            </p>
            <input
              type="date"
              value={visaAppointmentDate}
              onChange={(e) => setVisaAppointmentDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full md:w-64 bg-slate-800 border border-white/10 p-4 rounded-xl text-white focus:border-amber-500 outline-none"
            />
            <p className="text-sm text-slate-500 mt-3">
              Earliest available: today • Exact date confirmed within 48 hours after payment approval.
            </p>
          </div>

          {/* Payment Section */}
          <div className="bg-slate-900 border border-white/10 p-8 md:p-10 rounded-3xl">
            <h4 className="text-2xl md:text-3xl font-bold mb-6">Pay Visa Processing Fee: $3,500 USD</h4>

            {visaPaymentProof ? (
              <div className="text-center py-12">
                <CheckCircle size={72} className="text-emerald-500 mx-auto mb-6" />
                <h5 className="text-2xl md:text-3xl font-bold mb-4">Payment Proof Received</h5>
                <p className="text-lg text-slate-300 mb-8">
                  Thank you! Our team will review your $3,500 payment within 24–48 hours.
                  Once approved, we will start the visa process immediately.
                </p>
                <button
                  onClick={() => setCurrentPage('overview')}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all"
                >
                  Return to Dashboard
                </button>
              </div>
            ) : visaUploading ? (
              <div className="text-center py-12">
                <Loader2 size={60} className="animate-spin text-amber-500 mx-auto mb-6" />
                <p className="text-xl font-bold">Uploading proof...</p>
              </div>
            ) : (
              <>
                <p className="text-slate-300 text-lg mb-8">
                  Please make the $3,500 payment using one of the methods below, then upload your proof (screenshot, receipt, or transaction ID).
                </p>

                {/* Payment methods grid */}
                <div className="grid md:grid-cols-3 gap-6 mb-10">
                  <div className="bg-slate-800 p-6 rounded-2xl border border-white/10">
                    <h5 className="font-bold text-amber-400 text-xl mb-4">USDT (TRC20)</h5>
                    <p className="text-slate-400 mb-3">Send to:</p>
                    <div className="bg-slate-950 p-4 rounded-xl text-center break-all text-sm font-mono">
                      TXq8P8x8x8x8x8x8x8x8x8x8x8x8x8x8x8x8x {/* ← Replace with real address */}
                    </div>
                  </div>

                  <div className="bg-slate-800 p-6 rounded-2xl border border-white/10">
                    <h5 className="font-bold text-amber-400 text-xl mb-4">Bank Transfer</h5>
                    <div className="space-y-3 text-slate-300">
                      <p><strong>Bank:</strong> Commercial Bank of Ethiopia</p>
                      <p><strong>Account Name:</strong> GlobalTravel PLC</p>
                      <p><strong>Account Number:</strong> 1000123456789</p>
                    </div>
                  </div>

                  <div className="bg-slate-800 p-6 rounded-2xl border border-white/10">
                    <h5 className="font-bold text-amber-400 text-xl mb-4">TeleBirr / M-Pesa</h5>
                    <div className="space-y-3 text-slate-300">
                      <p><strong>Send to:</strong> 251911223344</p>
                      <p><strong>Amount:</strong> $3500 (or equivalent ETB)</p>
                      <p><strong>Reference:</strong> Your Full Name</p>
                    </div>
                  </div>
                </div>

                {/* Upload button */}
                <button
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*,.pdf';
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setVisaUploading(true);
                      const reader = new FileReader();
                      reader.onload = () => {
                        setVisaPaymentProof(reader.result);
                        setVisaUploading(false);
                        showNotification('Visa fee proof uploaded – awaiting approval', 'success');
                      };
                      reader.readAsDataURL(file);
                    };
                    input.click();
                  }}
                  className="w-full bg-amber-600 hover:bg-amber-500 text-white py-6 rounded-2xl font-bold text-xl transition-all shadow-lg mt-6"
                >
                  Upload Payment Proof ($3,500)
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
                   
      case 'overview':
        return (
          <div className="space-y-8">
            {/* Welcome Card */}
            <div className="bg-gradient-to-br from-indigo-600 to-violet-700 p-8 rounded-2xl text-white">
              <h3 className="text-3xl font-bold mb-4">Welcome to Your Dashboard</h3>
              <p className="text-indigo-100 mb-6">Track your application progress and complete the required steps.</p>
              <div className="flex gap-4">
                <button 
                  onClick={handleStartApplication}
                  className="bg-white text-indigo-900 px-6 py-3 rounded-xl font-bold hover:bg-indigo-50 transition-all"
                >
                  Continue Application
                </button>
              </div>
            </div>

            {/* Progress Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900 border border-white/10 p-6 rounded-xl">
                <h4 className="font-bold mb-4">Application Progress</h4>
                <div className="space-y-4">
                  {pages.filter(p => p.step).map(page => (
                    <div key={page.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${page.completed ? 'bg-emerald-500/20' : page.locked ? 'bg-slate-800' : 'bg-amber-500/20'}`}>
                          <page.icon size={16} className={page.completed ? 'text-emerald-500' : page.locked ? 'text-slate-600' : 'text-amber-500'} />
                        </div>
                        <span className={page.locked ? 'text-slate-600' : ''}>{page.name}</span>
                      </div>
                      {page.completed ? (
                        <CheckCircle size={16} className="text-emerald-500" />
                      ) : page.locked ? (
                        <Lock size={16} className="text-slate-600" />
                      ) : (
                        <span className="text-xs text-amber-500">In Progress</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-900 border border-white/10 p-6 rounded-xl">
                <h4 className="font-bold mb-4">Quick Actions</h4>
                <div className="space-y-3">
                  <button 
                    onClick={() => setCurrentPage('documents')}
                    className="w-full text-left p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all"
                  >
                    Upload Documents
                  </button>
                  <button 
                    onClick={() => setCurrentPage('video')}
                    className="w-full text-left p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all"
                  >
                    Submit Introduction Video
                  </button>
                  <button 
                    onClick={() => setCurrentPage('payment')}
                    className="w-full text-left p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all"
                  >
                    Make Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'kyc':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold">KYC Information</h3>
            {userData?.kyc ? (
              <div className="bg-slate-900 border border-white/10 p-6 rounded-xl">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(userData.kyc).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-xs text-slate-500 capitalize">{key}</p>
                      <p className="font-medium">{String(value)}</p>
                    </div>
                  ))}
                </div>
                <button 
                  onClick={() => navigate('kyc')}
                  className="mt-6 bg-amber-500 text-slate-950 px-6 py-3 rounded-xl font-bold hover:bg-amber-400"
                >
                  Edit Information
                </button>
              </div>
            ) : (
              <div className="bg-slate-900 border border-white/10 p-12 rounded-xl text-center">
                <User size={48} className="text-amber-500 mx-auto mb-4" />
                <p className="text-slate-400 mb-6">You haven't completed your KYC information yet.</p>
                <button 
                  onClick={() => navigate('kyc')}
                  className="bg-amber-500 text-slate-950 px-8 py-4 rounded-xl font-bold hover:bg-amber-400"
                >
                  Start KYC
                </button>
              </div>
            )}
          </div>
        );

      case 'education':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold">Education & Background</h3>
            {userData?.education ? (
              <div className="bg-slate-900 border border-white/10 p-6 rounded-xl">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(userData.education).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-xs text-slate-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                      <p className="font-medium">{String(value)}</p>
                    </div>
                  ))}
                </div>
                <button 
                  onClick={() => navigate('education')}
                  className="mt-6 bg-amber-500 text-slate-950 px-6 py-3 rounded-xl font-bold hover:bg-amber-400"
                >
                  Edit Information
                </button>
              </div>
            ) : (
              <div className="bg-slate-900 border border-white/10 p-12 rounded-xl text-center">
                <GraduationCap size={48} className="text-amber-500 mx-auto mb-4" />
                <p className="text-slate-400 mb-6">You haven't completed your education information yet.</p>
                <button 
                  onClick={() => navigate('education')}
                  className="bg-amber-500 text-slate-950 px-8 py-4 rounded-xl font-bold hover:bg-amber-400"
                >
                  Start Education
                </button>
              </div>
            )}
          </div>
        );

      case 'documents':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold">Documents</h3>
            {userData?.documents && Object.keys(userData.documents).length > 0 ? (
              <div className="bg-slate-900 border border-white/10 p-6 rounded-xl">
                <div className="space-y-4">
                  {Object.entries(userData.documents).map(([key, doc]) => (
                    <div key={key} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-amber-500" />
                        <div>
                          <p className="font-bold capitalize">{key}</p>
                          <p className="text-xs text-slate-400">{doc.name}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full ${
                        doc.status === 'approved' ? 'bg-emerald-500/20 text-emerald-500' : 
                        doc.status === 'rejected' ? 'bg-red-500/20 text-red-500' : 
                        'bg-amber-500/20 text-amber-500'
                      }`}>
                        {doc.status || 'pending'}
                      </span>
                    </div>
                  ))}
                </div>
                {Object.keys(userData.documents).length < 6 && (
                  <button 
                    onClick={() => navigate('documents')}
                    className="mt-6 w-full bg-amber-500 text-slate-950 px-6 py-3 rounded-xl font-bold hover:bg-amber-400"
                  >
                    Upload More Documents
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-slate-900 border border-white/10 p-12 rounded-xl text-center">
                <FileText size={48} className="text-amber-500 mx-auto mb-4" />
                <p className="text-slate-400 mb-6">You haven't uploaded any documents yet.</p>
                <button 
                  onClick={() => navigate('documents')}
                  className="bg-amber-500 text-slate-950 px-8 py-4 rounded-xl font-bold hover:bg-amber-400"
                >
                  Upload Documents
                </button>
              </div>
            )}
          </div>
        );

      case 'payment':
  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">{t.payment?.title || "Application Fee: $15 USD"}</h1>
        <p className="text-slate-400">{t.payment?.step || "Step 4 of 4"}</p>
        <div className="w-full bg-slate-800 h-2 rounded-full mt-6">
          <div className="bg-amber-500 h-2 rounded-full" style={{ width: '100%' }}></div>
        </div>
      </div>

      <div className="bg-slate-900 border border-white/10 p-12 rounded-[3rem] shadow-3xl space-y-8">
        {/* Payment methods (keep your existing grid) */}
        <div className="flex gap-4">
          <button
            onClick={() => setPaymentMethod('usdt')}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              paymentMethod === 'usdt' ? 'border-amber-500 bg-amber-500/10' : 'border-white/10 bg-white/5'
            }`}
          >
            USDT (TRC20)
          </button>
          <button
            onClick={() => setPaymentMethod('bank')}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              paymentMethod === 'bank' ? 'border-amber-500 bg-amber-500/10' : 'border-white/10 bg-white/5'
            }`}
          >
            Bank Transfer
          </button>
          <button
            onClick={() => setPaymentMethod('telebirr')}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              paymentMethod === 'telebirr' ? 'border-amber-500 bg-amber-500/10' : 'border-white/10 bg-white/5'
            }`}
          >
            TeleBirr / M-Pesa
          </button>
        </div>

        {/* Instructions + Upload */}
        <div className="bg-white/5 p-8 rounded-2xl">
          {/* ... your existing instructions for selected method ... */}

          {paymentProof ? (
            <div className="text-center py-12">
              <CheckCircle size={64} className="text-emerald-500 mx-auto mb-6" />
              <h5 className="text-2xl font-bold mb-4">Payment Proof Uploaded!</h5>
              <p className="text-xl text-amber-300 font-semibold mb-6">
                Please wait 10–30 minutes for admin approval
              </p>
              <p className="text-slate-400 mb-8">
                You will be notified once approved. You can safely close this page.
              </p>

              {/* Optional: Small spinner or timer animation */}
              <div className="flex justify-center items-center gap-3 text-slate-400">
                <Loader2 size={24} className="animate-spin" />
                <span>Waiting for confirmation...</span>
              </div>

              <button
                onClick={() => setCurrentPage('overview')}
                className="mt-10 bg-amber-600 hover:bg-amber-500 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all"
              >
                Back to Dashboard
              </button>
            </div>
          ) : (
            <>
              {/* Upload button */}
              <button
  onClick={handleFileUpload}
  disabled={uploading}
  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 py-5 rounded-2xl font-bold text-xl transition-all disabled:opacity-50"
>
  {uploading ? 'Uploading...' : 'Upload Payment Proof'}
</button>
            </>
          )}
        </div>
      </div>
    </div>
  );

     case 'video':
  return (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold">Introduction Video</h3>
      <div className="bg-slate-900 border border-white/10 p-8 rounded-xl">
        {videoSubmitted ? (
          <div className="text-center py-12">
            <CheckCircle size={64} className="text-emerald-500 mx-auto mb-6" />
            <h4 className="text-2xl font-bold mb-3">Video Submitted Successfully!</h4>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Your introduction video has been uploaded and is awaiting admin review.
            </p>
            <button
              onClick={() => setCurrentPage('overview')}
              className="bg-amber-500 text-slate-950 px-8 py-4 rounded-xl font-bold hover:bg-amber-400"
            >
              Back to Overview
            </button>
          </div>
        ) : (
          <>
            {/* Guidelines */}
            <div className="mb-8">
              <h4 className="font-bold mb-4 text-lg">Recording Guidelines:</h4>
              <ul className="space-y-3 text-slate-300 text-sm">
                <li className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-1" />
                  Introduce yourself (name, age, location, skills)
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-1" />
                  Explain why you want to work abroad
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-1" />
                  Keep it 1–3 minutes long
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-1" />
                  Professional attire, quiet & well-lit background
                </li>
              </ul>
            </div>

            {/* Upload Area */}
            <div className="border-2 border-dashed border-white/20 rounded-xl p-10 text-center mb-8 bg-slate-950/50">
              {uploading ? (
                <div className="py-10">
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 size={48} className="animate-spin text-amber-500" />
                    <div className="w-full max-w-md">
                      <div className="text-lg font-bold mb-2">
                        Uploading... {uploadProgress}%
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-amber-500 h-3 rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-sm text-slate-400 mt-3">
                        Please do not close this page
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <Video size={64} className="text-amber-500 mx-auto mb-6" />
                  <p className="text-xl font-bold mb-3">Upload Your Introduction Video</p>
                  <p className="text-sm text-slate-400 mb-6">
                    MP4, MOV, WebM • Max 100 MB • 1–3 minutes recommended
                  </p>

                  <label className="cursor-pointer inline-block mb-6">
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm"
                      className="hidden"
                      onChange={async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  if (file.size > 100 * 1024 * 1024) {
    showNotification('Video too large (max 100 MB)', 'error');
    return;
  }

  try {
    setUploading(true);
    setUploadProgress(0);

    const storageRef = ref(
      storage,
      `videos/${user.uid}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`
    );

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress(progress);
      },
      (error) => {
        console.error('Upload failed:', error);
        showNotification('Upload failed', 'error');
        setUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        // Save to Firestore...
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          'documents.video': {
            name: file.name,
            url: downloadURL,
            type: file.type,
            size: file.size,
            uploadedAt: Timestamp.now(),
            status: 'pending'
          },
          applicationStep: 6,
          updatedAt: Timestamp.now()
        });

        setVideoSubmitted(true);
        setApplicationStep(6);
        showNotification('Video uploaded!', 'success');
        setUploading(false);
      }
    );
  } catch (err) {
    console.error(err);
    showNotification('Error during upload', 'error');
    setUploading(false);
  }
}}
                    />
                    <div className="bg-amber-500 text-slate-950 px-8 py-4 rounded-xl font-bold hover:bg-amber-400 transition-all cursor-pointer">
                      Choose Video File
                    </div>
                  </label>

                  {/* New Skip & Submit Later Buttons */}
                  <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
                    <button
                      onClick={() => {
                        // Skip: advance step without uploading
                        setApplicationStep(6);
                        showNotification('Video step skipped. You can add it later in the portal.', 'info');
                        setCurrentPage('overview');
                      }}
                      className="bg-slate-700 text-slate-300 px-8 py-4 rounded-xl font-bold hover:bg-slate-600 transition-all"
                    >
                      Skip for now
                    </button>

                    <button
                      onClick={() => {
                        // Submit later: mark as pending + advance
                        setApplicationStep(6);
                        showNotification('Video marked as pending. Submit it anytime from the portal.', 'success');
                        setCurrentPage('overview');
                      }}
                      className="bg-amber-600/30 text-amber-300 border border-amber-500/50 px-8 py-4 rounded-xl font-bold hover:bg-amber-600/50 transition-all"
                    >
                      Submit later
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Pro Tip */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
              <h5 className="font-bold text-amber-400 mb-2">Pro Tip</h5>
              <p className="text-sm text-slate-300">
                You can always come back to upload your video later from the portal.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
      default:
        return null;
    }
  };
              // ======================
// WORK ACCEPTANCE MILESTONE (3–7 days)
// Appears after video (submitted or skipped) + application fee approved
// ======================
// Work Milestone (3–7 days)
const WorkMilestone = () => (
  <div className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
    <div className="bg-gradient-to-br from-emerald-950 to-slate-950 border border-emerald-500/40 rounded-2xl sm:rounded-3xl max-w-lg sm:max-w-xl w-full p-6 sm:p-10 text-center relative shadow-2xl shadow-emerald-900/30">
      
      {/* Close button */}
      <button
        onClick={() => setShowWorkMilestone(false)}
        className="absolute top-4 right-4 text-slate-400 hover:text-emerald-400 transition-colors"
      >
        <X size={28} />
      </button>

      {/* Icon - smaller */}
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
        <CheckCircle size={32} className="text-emerald-500 animate-pulse sm:size-40" />
      </div>

      <h2 className="text-3xl sm:text-4xl font-black mb-6 text-emerald-400 tracking-tight">
        Next Stage Started!
      </h2>

      <div className="space-y-6 text-slate-200 text-base sm:text-lg leading-relaxed max-w-md mx-auto">
        <p className="text-sm sm:text-base">
          Your documents and application fee have been approved.
        </p>

        <div className="bg-emerald-900/30 border border-emerald-500/30 p-6 sm:p-8 rounded-xl">
          <h3 className="text-2xl sm:text-3xl font-bold text-emerald-300 mb-4">
            Estimated Time
          </h3>
          <p className="text-4xl sm:text-5xl font-black text-white mb-4">
            3–7 Days
          </p>
          <p className="text-lg sm:text-xl font-semibold mb-4">for:</p>
          
          <ul className="space-y-4 text-left text-base sm:text-lg">
            <li className="flex items-start gap-3">
              <CheckCircle size={24} className="text-emerald-400 shrink-0 mt-1" />
              Work Acceptance Letter from employer
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle size={24} className="text-emerald-400 shrink-0 mt-1" />
              Work Permit / Visa issuance
            </li>
          </ul>
        </div>

        <p className="text-lg sm:text-xl font-semibold text-amber-300 mt-6">
          After receiving both documents, you can apply for the visa at the embassy.
        </p>

        <p className="text-slate-400 text-sm sm:text-base mt-4">
          We'll notify you via email and portal when ready.
        </p>
      </div>

      <button
        onClick={() => {
          setShowWorkMilestone(false);
          setCurrentPage('overview');
        }}
        className="mt-8 sm:mt-10 bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-4 sm:px-12 sm:py-5 rounded-xl font-bold text-base sm:text-lg transition-all shadow-lg"
      >
        Back to Dashboard
      </button>
    </div>
  </div>
);

// Visa Milestone (1–4 months)
const VisaMilestone = () => (
  <div className="fixed inset-0 z-[200] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
    <div className="bg-gradient-to-br from-blue-950 to-slate-950 border border-blue-500/40 rounded-2xl sm:rounded-3xl max-w-lg sm:max-w-xl w-full p-6 sm:p-10 text-center relative shadow-2xl shadow-blue-900/30">
      
      {/* Close button */}
      <button
        onClick={() => setShowVisaMilestone(false)}
        className="absolute top-4 right-4 text-slate-400 hover:text-blue-400 transition-colors"
      >
        <X size={28} />
      </button>

      {/* Icon - smaller */}
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
        <Loader2 size={32} className="text-blue-400 animate-spin sm:size-40" />
      </div>

      <h2 className="text-3xl sm:text-4xl font-black mb-6 text-blue-400 tracking-tight">
        Visa Application In Progress
      </h2>

      <div className="space-y-6 text-slate-200 text-base sm:text-lg leading-relaxed max-w-md mx-auto">
        <p className="text-sm sm:text-base">
          Your $3,500 visa processing fee has been received.
        </p>

        <div className="bg-blue-900/30 border border-blue-500/30 p-6 sm:p-8 rounded-xl">
          <h3 className="text-2xl sm:text-3xl font-bold text-blue-300 mb-4">
            Current Status
          </h3>
          <p className="text-lg sm:text-xl font-semibold mb-4">
            Visa application undergoing inspection by the country
          </p>
          <p className="text-4xl sm:text-5xl font-black text-white mb-6">
            1–4 Months
          </p>
        </div>

        <p className="text-slate-400 text-sm sm:text-base">
          We will notify you immediately once there is an update.
        </p>
      </div>

      <button
        onClick={() => {
          setShowVisaMilestone(false);
          setCurrentPage('overview');
        }}
        className="mt-8 sm:mt-10 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 sm:px-12 sm:py-5 rounded-xl font-bold text-base sm:text-lg transition-all shadow-lg"
      >
        Back to Dashboard
      </button>
    </div>
  </div>
);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      {/* Header with Progress */}
      <div className="bg-slate-900 border border-white/10 p-6 rounded-xl mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center text-2xl font-bold text-slate-950">
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-2xl font-bold">{t.portal.welcome} {user?.email?.split('@')[0] || 'Candidate'}</h2>
              <p className="text-slate-400">Application ID: {user?.uid?.slice(0, 8)}...</p>
            </div>
          </div>
          <div className="w-full md:w-64">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Overall Progress</span>
              <span className="text-amber-500 font-bold">{Math.round(progress)}%</span>
            </div>
            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar and Content */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 space-y-2">
          {pages.map(page => (
            <button
              key={page.id}
              onClick={() => !page.locked && setCurrentPage(page.id)}
              className={`w-full flex items-center gap-3 p-4 rounded-xl transition-all ${
                currentPage === page.id 
                  ? 'bg-amber-500 text-slate-950' 
                  : page.locked 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-white/5'
              }`}
              disabled={page.locked}
            >
              <page.icon size={18} />
              <span className="flex-1 text-left">{page.name}</span>
              {page.completed && !page.locked && (
                <CheckCircle size={16} className="text-emerald-500" />
              )}
              {page.locked && (
                <Lock size={14} className="text-slate-600" />
              )}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {renderPage()}
        </div>
      </div>
    {showWorkMilestone && <WorkMilestone />}
    {showVisaMilestone && <VisaMilestone />}
    </div>
  );
}
// ============= ADMIN VIEW WITH FULL PRIVILEGES =============
function AdminView({ t, user, navigate, showNotification }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersList = [];
      querySnapshot.forEach((doc) => {
        usersList.push({ id: doc.id, ...doc.data() });
      });
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      showNotification('Error loading users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ===== USER MANAGEMENT =====
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'users', selectedUser.id));
      showNotification(`User ${selectedUser.email} deleted successfully`, 'success');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Error deleting user: ' + error.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // ===== PAYMENT MANAGEMENT =====
  const handleApprovePayment = async () => {
    if (!selectedUser || !selectedPayment) return;
    
    setProcessing(true);
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      const updatedPayments = userData.payments.map(p => 
        p.id === selectedPayment.id ? { ...p, status: 'approved', approvedAt: new Date().toISOString() } : p
      );
      
      await updateDoc(userRef, { payments: updatedPayments });
      
      showNotification(`Payment $${selectedPayment.amount} approved successfully!`, 'success');
      setShowApproveModal(false);
      setSelectedPayment(null);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      showNotification('Error approving payment', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectPayment = async () => {
    if (!selectedUser || !selectedPayment) return;
    
    setProcessing(true);
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      const updatedPayments = userData.payments.map(p => 
        p.id === selectedPayment.id ? { ...p, status: 'rejected', rejectedAt: new Date().toISOString() } : p
      );
      
      await updateDoc(userRef, { payments: updatedPayments });
      
      showNotification(`Payment rejected`, 'info');
      setShowRejectModal(false);
      setSelectedPayment(null);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      showNotification('Error rejecting payment', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const openApprovePayment = (user, payment) => {
    setSelectedUser(user);
    setSelectedPayment(payment);
    setShowApproveModal(true);
  };

  const openRejectPayment = (user, payment) => {
    setSelectedUser(user);
    setSelectedPayment(payment);
    setShowRejectModal(true);
  };

  // ===== DOCUMENT MANAGEMENT =====
  const handleApproveDocument = async () => {
    if (!selectedUser || !selectedDocument) return;
    
    setProcessing(true);
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        [`documents.${selectedDocument}.status`]: 'approved',
        [`documents.${selectedDocument}.approvedAt`]: new Date().toISOString()
      });
      
      showNotification(`Document approved successfully!`, 'success');
      setShowApproveModal(false);
      setSelectedDocument(null);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      showNotification('Error approving document', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectDocument = async () => {
    if (!selectedUser || !selectedDocument) return;
    
    setProcessing(true);
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        [`documents.${selectedDocument}.status`]: 'rejected',
        [`documents.${selectedDocument}.rejectedAt`]: new Date().toISOString()
      });
      
      showNotification(`Document rejected`, 'info');
      setShowRejectModal(false);
      setSelectedDocument(null);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      showNotification('Error rejecting document', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const openApproveDocument = (user, docId) => {
    setSelectedUser(user);
    setSelectedDocument(docId);
    setShowApproveModal(true);
  };

  const openRejectDocument = (user, docId) => {
    setSelectedUser(user);
    setSelectedDocument(docId);
    setShowRejectModal(true);
  };

  // ===== APPLICATION STATUS =====
  const handleUpdateStatus = async (userId, newStatus) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 
        status: newStatus,
        updatedAt: Timestamp.now()
      });
      showNotification(`User status updated to ${newStatus}`, 'success');
      loadUsers();
    } catch (error) {
      showNotification('Error updating status', 'error');
    }
  };

  // ===== STATS CALCULATION =====
  const totalUsers = users.length;
  const pendingPayments = users.filter(u => 
    u.payments?.some(p => p.status === 'pending')
  ).length;
  const pendingDocuments = users.filter(u => 
    u.documents && Object.values(u.documents).some(d => !d.status || d.status === 'pending')
  ).length;
  const approvedApplications = users.filter(u => 
    u.payment?.status === 'approved' && u.documents && Object.keys(u.documents).length >= 6
  ).length;
  const verifiedUsers = users.filter(u => u.status === 'active' || u.payment?.status === 'approved').length;

  // ===== FILTERED USERS =====
  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const viewUserDetails = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 text-center">
        <Loader2 size={48} className="animate-spin text-amber-500 mx-auto" />
        <p className="mt-4 text-slate-400">Loading admin panel...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Admin Header */}
      <div className="bg-gradient-to-br from-amber-600 to-amber-800 p-8 rounded-2xl text-white">
        <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-amber-100">Welcome back, {user?.email}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-slate-900 border border-white/10 p-4 rounded-xl text-center">
          <Users size={24} className="text-amber-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{totalUsers}</p>
          <p className="text-xs text-slate-400">Total Users</p>
        </div>
        <div className="bg-slate-900 border border-white/10 p-4 rounded-xl text-center">
          <CheckCircle size={24} className="text-emerald-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{verifiedUsers}</p>
          <p className="text-xs text-slate-400">Verified</p>
        </div>
        <div className="bg-slate-900 border border-white/10 p-4 rounded-xl text-center">
          <CreditCard size={24} className="text-amber-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{pendingPayments}</p>
          <p className="text-xs text-slate-400">Pending Pay</p>
        </div>
        <div className="bg-slate-900 border border-white/10 p-4 rounded-xl text-center">
          <FileText size={24} className="text-amber-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{pendingDocuments}</p>
          <p className="text-xs text-slate-400">Pending Docs</p>
        </div>
        <div className="bg-slate-900 border border-white/10 p-4 rounded-xl text-center">
          <Award size={24} className="text-emerald-500 mx-auto mb-2" />
          <p className="text-2xl font-bold">{approvedApplications}</p>
          <p className="text-xs text-slate-400">Approved</p>
        </div>
        <div className="bg-slate-900 border border-white/10 p-4 rounded-xl text-center">
          <RefreshCw size={24} className="text-blue-500 mx-auto mb-2 cursor-pointer hover:rotate-180 transition-all duration-500" 
            onClick={loadUsers} />
          <p className="text-xs text-slate-400">Refresh</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-800 border border-white/10 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:border-amber-500"
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-4 flex-wrap">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
            activeTab === 'users' ? 'bg-amber-500 text-slate-950' : 'bg-white/5 hover:bg-white/10'
          }`}
        >
          All Users ({filteredUsers.length})
        </button>
        <button
          onClick={() => setActiveTab('pending-payments')}
          className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
            activeTab === 'pending-payments' ? 'bg-amber-500 text-slate-950' : 'bg-white/5 hover:bg-white/10'
          }`}
        >
          Pending Payments ({pendingPayments})
        </button>
        <button
          onClick={() => setActiveTab('pending-documents')}
          className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
            activeTab === 'pending-documents' ? 'bg-amber-500 text-slate-950' : 'bg-white/5 hover:bg-white/10'
          }`}
        >
          Pending Documents ({pendingDocuments})
        </button>
      </div>

      {/* Content based on tab */}
      <div className="space-y-6">
        {/* ALL USERS TAB */}
        {activeTab === 'users' && (
          <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4 text-sm font-bold">Name</th>
                    <th className="text-left p-4 text-sm font-bold">Email</th>
                    <th className="text-left p-4 text-sm font-bold">Joined</th>
                    <th className="text-left p-4 text-sm font-bold">Status</th>
                    <th className="text-left p-4 text-sm font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-4 font-medium">{user.name || 'N/A'}</td>
                      <td className="p-4 text-sm text-slate-300">{user.email}</td>
                      <td className="p-4 text-sm text-slate-400">
                        {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="p-4">
                        <select
                          value={user.status || 'pending'}
                          onChange={(e) => handleUpdateStatus(user.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full border-0 font-bold ${
                            user.status === 'active' || user.payment?.status === 'approved' 
                              ? 'bg-emerald-500/20 text-emerald-500' 
                              : user.status === 'rejected' 
                                ? 'bg-red-500/20 text-red-500'
                                : 'bg-amber-500/20 text-amber-500'
                          }`}
                        >
                          <option value="pending">Pending</option>
                          <option value="active">Active</option>
                          <option value="reviewing">Reviewing</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => viewUserDetails(user)}
                            className="text-blue-400 hover:text-blue-300 text-sm px-3 py-1 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 flex items-center gap-1"
                            title="View Details"
                          >
                            <Eye size={14} /> View
                          </button>
                          <button
                            onClick={() => confirmDelete(user)}
                            className="text-red-400 hover:text-red-300 text-sm px-3 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center gap-1"
                            title="Delete User"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PENDING PAYMENTS TAB */}
        {activeTab === 'pending-payments' && (
          <div className="space-y-4">
            {users.filter(u => u.payments?.some(p => p.status === 'pending')).length === 0 ? (
              <div className="bg-slate-900 border border-white/10 p-12 rounded-xl text-center">
                <CheckCircle size={48} className="text-emerald-500 mx-auto mb-4" />
                <p className="text-xl font-bold">No Pending Payments</p>
                <p className="text-slate-400 mt-2">All payments have been processed.</p>
              </div>
            ) : (
              users.filter(u => u.payments?.some(p => p.status === 'pending')).map(user => (
                <div key={user.id} className="bg-slate-900 border border-white/10 p-6 rounded-xl">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{user.name || 'Unnamed'}</h3>
                      <p className="text-sm text-slate-400">{user.email}</p>
                    </div>
                    <span className="bg-amber-500/20 text-amber-500 px-3 py-1 rounded-full text-xs font-bold">
                      {user.payments?.filter(p => p.status === 'pending').length} pending
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    {user.payments?.filter(p => p.status === 'pending').map((payment, idx) => (
                      <div key={idx} className="bg-white/5 p-4 rounded-lg">
                        <div className="flex flex-wrap justify-between items-center gap-4">
                          <div>
                            <p className="text-lg font-bold text-amber-500">${payment.amount}</p>
                            <p className="text-xs text-slate-400">Method: {payment.method}</p>
                            <p className="text-xs text-slate-400">
                              {new Date(payment.uploadedAt).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openApprovePayment(user, payment)}
                              className="bg-emerald-500/20 text-emerald-500 px-4 py-2 rounded-lg font-bold text-sm hover:bg-emerald-500/30 flex items-center gap-1"
                            >
                              <ThumbsUp size={14} /> Approve
                            </button>
                            <button
                              onClick={() => openRejectPayment(user, payment)}
                              className="bg-red-500/20 text-red-500 px-4 py-2 rounded-lg font-bold text-sm hover:bg-red-500/30 flex items-center gap-1"
                            >
                              <ThumbsDown size={14} /> Reject
                            </button>
                          </div>
                        </div>
                        {payment.proof && (
                          <button 
                            onClick={() => window.open(payment.proof, '_blank')}
                            className="mt-3 text-amber-500 text-sm flex items-center gap-1 hover:underline"
                          >
                            <ExternalLink size={14} /> View Payment Proof
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* PENDING DOCUMENTS TAB */}
        {activeTab === 'pending-documents' && (
          <div className="space-y-4">
            {users.filter(u => u.documents && Object.values(u.documents).some(d => !d.status || d.status === 'pending')).length === 0 ? (
              <div className="bg-slate-900 border border-white/10 p-12 rounded-xl text-center">
                <CheckCircle size={48} className="text-emerald-500 mx-auto mb-4" />
                <p className="text-xl font-bold">No Pending Documents</p>
                <p className="text-slate-400 mt-2">All documents have been reviewed.</p>
              </div>
            ) : (
              users.filter(u => u.documents && Object.values(u.documents).some(d => !d.status || d.status === 'pending')).map(user => (
                <div key={user.id} className="bg-slate-900 border border-white/10 p-6 rounded-xl">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">{user.name || user.email}</h3>
                    <span className="bg-amber-500/20 text-amber-500 px-3 py-1 rounded-full text-xs font-bold">
                      {Object.values(user.documents).filter(d => !d.status || d.status === 'pending').length} pending
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {user.documents && Object.entries(user.documents).map(([docId, doc]) => (
                      (!doc.status || doc.status === 'pending') && (
                        <div key={docId} className="bg-white/5 p-4 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-bold capitalize">{docId}</p>
                              <p className="text-xs text-slate-400 truncate max-w-[150px]">{doc.name}</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => openApproveDocument(user, docId)}
                                className="bg-emerald-500/20 text-emerald-500 px-3 py-1 rounded-lg text-xs font-bold hover:bg-emerald-500/30"
                                title="Approve"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => openRejectDocument(user, docId)}
                                className="bg-red-500/20 text-red-500 px-3 py-1 rounded-lg text-xs font-bold hover:bg-red-500/30"
                                title="Reject"
                              >
                                ✗
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Delete User</h3>
              <p className="text-slate-400 mb-4">
                Are you sure you want to delete <span className="text-amber-500 font-bold">{selectedUser.email}</span>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 bg-white/5 rounded-lg font-bold hover:bg-white/10"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-500 text-slate-950 rounded-lg font-bold hover:bg-red-400 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  {deleting ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* APPROVE/REJECT MODAL */}
      {(showApproveModal || showRejectModal) && selectedUser && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className={`w-16 h-16 ${showApproveModal ? 'bg-emerald-500/20' : 'bg-red-500/20'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                {showApproveModal ? (
                  <ThumbsUp size={32} className="text-emerald-500" />
                ) : (
                  <ThumbsDown size={32} className="text-red-500" />
                )}
              </div>
              <h3 className="text-xl font-bold mb-2">
                {showApproveModal ? 'Approve' : 'Reject'}
              </h3>
              <p className="text-slate-400 mb-4">
                {selectedPayment ? (
                  <>Are you sure you want to {showApproveModal ? 'approve' : 'reject'} payment of <span className="text-amber-500 font-bold">${selectedPayment.amount}</span>?</>
                ) : selectedDocument ? (
                  <>Are you sure you want to {showApproveModal ? 'approve' : 'reject'} <span className="text-amber-500 font-bold capitalize">{selectedDocument}</span> document?</>
                ) : (
                  <>Are you sure you want to {showApproveModal ? 'approve' : 'reject'} this user?</>
                )}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setShowRejectModal(false);
                    setSelectedPayment(null);
                    setSelectedDocument(null);
                  }}
                  className="flex-1 px-4 py-2 bg-white/5 rounded-lg font-bold hover:bg-white/10"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  onClick={showApproveModal ? 
                    (selectedPayment ? handleApprovePayment : handleApproveDocument) : 
                    (selectedPayment ? handleRejectPayment : handleRejectDocument)
                  }
                  disabled={processing}
                  className={`flex-1 px-4 py-2 rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${
                    showApproveModal 
                      ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400' 
                      : 'bg-red-500 text-slate-950 hover:bg-red-400'
                  }`}
                >
                  {processing ? <Loader2 size={16} className="animate-spin" /> : (showApproveModal ? 'Approve' : 'Reject')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* USER DETAILS MODAL */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4 sticky top-0 bg-slate-900 pb-2 border-b border-white/10">
              <h3 className="text-xl font-bold">User Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-1 bg-white/5 rounded-lg hover:bg-white/10"
              >
                <X size={18} />
              </button>
            </div>

            {/* User Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Name</p>
                <p className="font-medium">{selectedUser.name || 'Not provided'}</p>
              </div>
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Email</p>
                <p className="font-medium break-all">{selectedUser.email}</p>
              </div>
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">User ID</p>
                <p className="text-xs font-mono break-all">{selectedUser.id}</p>
              </div>
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Joined</p>
                <p className="font-medium">
                  {selectedUser.createdAt ? new Date(selectedUser.createdAt.seconds * 1000).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Status</p>
                <select
                  value={selectedUser.status || 'pending'}
                  onChange={(e) => {
                    handleUpdateStatus(selectedUser.id, e.target.value);
                    setSelectedUser({...selectedUser, status: e.target.value});
                  }}
                  className={`text-sm px-3 py-1 rounded-full border-0 font-bold ${
                    selectedUser.status === 'active' || selectedUser.payment?.status === 'approved' 
                      ? 'bg-emerald-500/20 text-emerald-500' 
                      : selectedUser.status === 'rejected' 
                        ? 'bg-red-500/20 text-red-500'
                        : 'bg-amber-500/20 text-amber-500'
                  }`}
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* KYC Info */}
            {selectedUser.kyc && Object.keys(selectedUser.kyc).length > 0 && (
              <div className="mb-6">
                <h4 className="font-bold mb-3 text-amber-500 flex items-center gap-2">
                  <User size={16} /> KYC Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(selectedUser.kyc).map(([key, value]) => (
                    <div key={key} className="bg-white/5 p-3 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                      <p className="text-sm">{String(value) || 'N/A'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {selectedUser.education && Object.keys(selectedUser.education).length > 0 && (
              <div className="mb-6">
                <h4 className="font-bold mb-3 text-amber-500 flex items-center gap-2">
                  <GraduationCap size={16} /> Education & Background
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(selectedUser.education).map(([key, value]) => (
                    <div key={key} className="bg-white/5 p-3 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                      <p className="text-sm">{String(value) || 'N/A'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {selectedUser.documents && Object.keys(selectedUser.documents).length > 0 && (
              <div className="mb-6">
                <h4 className="font-bold mb-3 text-amber-500 flex items-center gap-2">
                  <FileText size={16} /> Documents
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(selectedUser.documents).map(([key, doc]) => (
                    <div key={key} className="bg-white/5 p-3 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold capitalize text-sm">{key}</p>
                          <p className="text-xs text-slate-400 truncate max-w-[150px]">{doc.name}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            doc.status === 'approved' ? 'bg-emerald-500/20 text-emerald-500' : 
                            doc.status === 'rejected' ? 'bg-red-500/20 text-red-500' : 
                            'bg-amber-500/20 text-amber-500'
                          }`}>
                            {doc.status || 'pending'}
                          </span>
                         {/* In the USER DETAILS MODAL, find the documents section */}
{doc.data && (
  <button 
    onClick={() => {
      if (doc.type?.startsWith('video/')) {
        // For videos, create a video player
        const win = window.open();
        win.document.write(`
          <html>
            <head><title>${doc.name}</title></head>
            <body style="margin:0; background:#000; display:flex; align-items:center; justify-content:center; min-height:100vh;">
              <video src="${doc.data}" controls style="max-width:100%; max-height:90vh;"></video>
            </body>
          </html>
        `);
      } else if (doc.data.startsWith('data:image')) {
        // For images
        const win = window.open();
        win.document.write(`<img src="${doc.data}" style="max-width:100%;"/>`);
      } else {
        // For other files
        window.open(doc.data, '_blank');
      }
    }}
    className="text-amber-500 text-xs hover:underline"
    title="View Document"
  >
    <Eye size={12} />
  </button>
)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payments */}
            {selectedUser.payments && selectedUser.payments.length > 0 && (
              <div className="mb-6">
                <h4 className="font-bold mb-3 text-amber-500 flex items-center gap-2">
                  <CreditCard size={16} /> Payment History
                </h4>
                <div className="space-y-2">
                  {selectedUser.payments.map((payment, idx) => (
                    <div key={idx} className="bg-white/5 p-3 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-amber-500">${payment.amount}</p>
                          <p className="text-xs text-slate-400">Method: {payment.method}</p>
                          <p className="text-xs text-slate-500">
                            {payment.uploadedAt ? new Date(payment.uploadedAt).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          payment.status === 'approved' ? 'bg-emerald-500/20 text-emerald-500' : 
                          payment.status === 'rejected' ? 'bg-red-500/20 text-red-500' : 
                          'bg-amber-500/20 text-amber-500'
                        }`}>
                          {payment.status || 'pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
              
// ============= KYC VIEW =============
function KYCView({ t, user, navigate, showNotification, setApplicationStep }) {
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    gender: '',
    nationality: 'Ethiopian',
    phoneNumber: '',
    address: '',
    city: '',
    country: 'Ethiopia'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        kyc: formData,
        applicationStep: 2,
        updatedAt: Timestamp.now()
      });
      
      setApplicationStep(2);
      showNotification('KYC information saved!', 'success');
      navigate('education');
    } catch (error) {
      showNotification('Error saving data', 'error');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">{t.kyc?.title || "Basic KYC Information"}</h1>
        <p className="text-slate-400">{t.kyc?.step || "Step 1 of 4"}</p>
        <div className="w-full bg-slate-800 h-2 rounded-full mt-6">
          <div className="bg-amber-500 h-2 rounded-full" style={{ width: '25%' }}></div>
        </div>
      </div>

      <div className="bg-slate-900 border border-white/10 p-12 rounded-[3rem] shadow-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm mb-2">{t.kyc?.fullName || "Full Name"} *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">{t.kyc?.dateOfBirth || "Date of Birth"} *</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">{t.kyc?.gender || "Gender"} *</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
              >
                <option value="">Select gender</option>
                <option value="male">{t.kyc?.male || "Male"}</option>
                <option value="female">{t.kyc?.female || "Female"}</option>
                <option value="other">{t.kyc?.other || "Other"}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm mb-2">{t.kyc?.nationality || "Nationality"} *</label>
              <select
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                required
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
              >
                <option value="Ethiopian">Ethiopian</option>
                <option value="Eritrean">Eritrean</option>
                <option value="Kenyan">Kenyan</option>
                <option value="Somali">Somali</option>
                <option value="Sudanese">Sudanese</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm mb-2">{t.kyc?.phoneNumber || "Phone Number"} *</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
                placeholder="+251 912 345678"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-2">{t.kyc?.address || "Address"} *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
                placeholder="Street address"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">{t.kyc?.city || "City"} *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
                placeholder="City"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">{t.kyc?.country || "Country"} *</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
                placeholder="Country"
              />
            </div>
          </div>

          <div className="flex justify-end mt-8">
            <button type="submit" className="bg-amber-500 text-slate-950 px-10 py-5 rounded-2xl font-black flex items-center gap-3">
              {t.kyc?.continue || "Continue to Education"}
              <ArrowRight size={18} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============= EDUCATION VIEW =============
function EducationView({ t, user, navigate, showNotification, setApplicationStep }) {
  const [formData, setFormData] = useState({
    highestEducation: '',
    fieldOfStudy: '',
    institution: '',
    graduationYear: '',
    grade: '',
    skills: '',
    languages: '',
    workExperience: '',
    currentEmployment: ''
  });

  const educationLevels = t.education?.levels || [
    "No Formal Education",
    "Primary School",
    "Secondary School",
    "High School Diploma",
    "Vocational Training",
    "Diploma",
    "Bachelor's Degree",
    "Master's Degree",
    "PhD",
    "Professional Certification"
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        education: formData,
        applicationStep: 3,
        updatedAt: Timestamp.now()
      });
      
      setApplicationStep(3);
      showNotification('Education information saved!', 'success');
      navigate('documents');
    } catch (error) {
      showNotification('Error saving data', 'error');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">{t.education?.title || "Education & Background"}</h1>
        <p className="text-slate-400">{t.education?.step || "Step 2 of 4"}</p>
        <div className="w-full bg-slate-800 h-2 rounded-full mt-6">
          <div className="bg-amber-500 h-2 rounded-full" style={{ width: '50%' }}></div>
        </div>
      </div>

      <div className="bg-slate-900 border border-white/10 p-12 rounded-[3rem] shadow-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm mb-2">{t.education?.highestEducation || "Highest Education Level"} *</label>
            <select
              name="highestEducation"
              value={formData.highestEducation}
              onChange={handleChange}
              required
              className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
            >
              <option value="">Select education level</option>
              {educationLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-2">{t.education?.fieldOfStudy || "Field of Study"} *</label>
            <input
              type="text"
              name="fieldOfStudy"
              value={formData.fieldOfStudy}
              onChange={handleChange}
              required
              className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
              placeholder="e.g., Electrical Engineering, Nursing, etc."
            />
          </div>

          <div>
            <label className="block text-sm mb-2">{t.education?.institution || "Institution Name"} *</label>
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              required
              className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
              placeholder="School/University name"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm mb-2">{t.education?.graduationYear || "Graduation Year"} *</label>
              <input
                type="number"
                name="graduationYear"
                value={formData.graduationYear}
                onChange={handleChange}
                required
                min="1950"
                max="2024"
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
                placeholder="YYYY"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">{t.education?.grade || "Grade/GPA (Optional)"}</label>
              <input
                type="text"
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
                placeholder="e.g., 3.5 GPA, Very Good, etc."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-2">{t.education?.skills || "Skills (comma separated)"} *</label>
            <input
              type="text"
              name="skills"
              value={formData.skills}
              onChange={handleChange}
              required
              className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
              placeholder="e.g., Welding, Electrical wiring, Patient care, etc."
            />
          </div>

          <div>
            <label className="block text-sm mb-2">{t.education?.languages || "Languages (comma separated)"} *</label>
            <input
              type="text"
              name="languages"
              value={formData.languages}
              onChange={handleChange}
              required
              className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
              placeholder="e.g., English, Amharic, Arabic"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">{t.education?.workExperience || "Work Experience"} *</label>
            <textarea
              name="workExperience"
              value={formData.workExperience}
              onChange={handleChange}
              required
              rows="3"
              className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
              placeholder="Describe your work experience..."
            />
          </div>

          <div>
            <label className="block text-sm mb-2">{t.education?.currentEmployment || "Current Employment Status"} *</label>
            <select
              name="currentEmployment"
              value={formData.currentEmployment}
              onChange={handleChange}
              required
              className="w-full bg-slate-950 border border-white/5 p-4 rounded-2xl focus:outline-none focus:border-amber-500"
            >
              <option value="">Select status</option>
              <option value="employed">{t.education?.employed || "Employed Full-time"}</option>
              <option value="part-time">{t.education?.partTime || "Employed Part-time"}</option>
              <option value="self-employed">{t.education?.selfEmployed || "Self-employed"}</option>
              <option value="unemployed">{t.education?.unemployed || "Unemployed"}</option>
              <option value="student">{t.education?.student || "Student"}</option>
            </select>
          </div>

          <div className="flex justify-end mt-8">
            <button type="submit" className="bg-amber-500 text-slate-950 px-10 py-5 rounded-2xl font-black flex items-center gap-3">
              {t.education?.continue || "Continue to Documents"}
              <ArrowRight size={18} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============= DOCUMENTS VIEW =============
function DocumentsView({ t, user, navigate, showNotification, setApplicationStep }) {
  const [uploading, setUploading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  
  const requiredDocs = t.documents?.list || [
    { id: 'passport', name: 'Passport / ID Card', description: 'Clear photo of your passport or national ID', accept: '.pdf,.jpg,.jpeg,.png' },
    { id: 'birthCertificate', name: 'Birth Certificate', description: 'Official birth certificate', accept: '.pdf,.jpg,.jpeg,.png' },
    { id: 'photo', name: 'Passport Photo', description: 'Recent passport-sized photo', accept: '.jpg,.jpeg,.png' },
    { id: 'education', name: 'Education Certificate', description: 'Highest education certificate', accept: '.pdf,.jpg,.jpeg,.png' },
    { id: 'work', name: 'Work Experience Letter', description: 'Letter from previous employer', accept: '.pdf,.jpg,.jpeg,.png' },
    { id: 'cv', name: 'CV / Resume', description: 'Your updated CV', accept: '.pdf,.doc,.docx' },
    { id: 'video', name: 'Introduction Video', description: 'Record a short video introducing yourself', accept: '.mp4,.mov,.avi,.webm' }
  ];

  const handleFileUpload = (docId) => {
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    
    // Find the document to get its accepted file types
    const doc = requiredDocs.find(d => d.id === docId);
    input.accept = doc?.accept || '.pdf,.jpg,.jpeg,.png';
    input.style.display = 'none';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Check file size (50MB max for videos, 10MB for others)
      const maxSize = docId === 'video' ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showNotification(`File too large. Maximum size is ${maxSize / (1024 * 1024)}MB.`, 'error');
        return;
      }
      
      setUploading(true);
      setUploadProgress({ ...uploadProgress, [docId]: 0 });
      
      // Read the file
      const reader = new FileReader();
      
      reader.onloadstart = () => {
        setUploadProgress({ ...uploadProgress, [docId]: 10 });
      };
      
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 90) + 10;
          setUploadProgress({ ...uploadProgress, [docId]: progress });
        }
      };
      
      reader.onload = async () => {
        const fileData = reader.result;
        
        // Simulate final processing
        setTimeout(() => {
          setUploadProgress({ ...uploadProgress, [docId]: 100 });
          
          // Save to state
          setUploadedDocs({
            ...uploadedDocs,
            [docId]: {
              name: file.name,
              data: fileData,
              type: file.type,
              size: file.size,
              uploadedAt: new Date().toISOString(),
              status: 'pending'
            }
          });
          
          setUploading(false);
          showNotification(`${doc.name} uploaded successfully!`, 'success');
          
          // Send email notification
          sendAdminEmail('document_upload', {
            userName: user.email,
            userEmail: user.email,
            documentType: doc.name,
            fileName: file.name,
            fileSize: file.size
          });
          
          // Clear progress after 2 seconds
          setTimeout(() => {
            setUploadProgress({ ...uploadProgress, [docId]: null });
          }, 2000);
        }, 500);
      };
      
      reader.onerror = () => {
        setUploading(false);
        setUploadProgress({ ...uploadProgress, [docId]: null });
        showNotification('Error reading file. Please try again.', 'error');
      };
      
      // Read the file
      reader.readAsDataURL(file);
    };
    
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  const handleContinue = async () => {
    const uploadedCount = Object.keys(uploadedDocs).length;
    
    // Check if all documents are uploaded
    if (uploadedCount < requiredDocs.length) {
      showNotification(`Please upload all ${requiredDocs.length} required documents`, 'error');
      return;
    }
    
    try {
      // Save to Firebase
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        documents: uploadedDocs,
        applicationStep: 4,
        updatedAt: Timestamp.now()
      });
      
      setApplicationStep(4);
      showNotification('Documents saved! Proceeding to payment...', 'success');
      navigate('payment');
    } catch (error) {
      console.error('Error saving documents:', error);
      showNotification('Error saving documents. Please try again.', 'error');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">{t.documents?.title || "Upload Documents"}</h1>
        <p className="text-slate-400">{t.documents?.step || "Step 3 of 4"}</p>
        
        {/* Progress bar */}
        <div className="w-full bg-slate-800 h-2 rounded-full mt-6">
          <div className="bg-amber-500 h-2 rounded-full transition-all duration-500" 
               style={{ width: `${(Object.keys(uploadedDocs).length / requiredDocs.length) * 100}%` }} />
        </div>
        
        <p className="text-sm text-slate-500 mt-4">
          Uploaded: {Object.keys(uploadedDocs).length} / {requiredDocs.length}
        </p>
      </div>

      <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl">
        <div className="space-y-4">
          {requiredDocs.map((doc) => {
            const isUploaded = uploadedDocs[doc.id];
            const progress = uploadProgress[doc.id];
            
            return (
              <div key={doc.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-800/50 rounded-xl border transition-all ${
                isUploaded ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/10 hover:border-amber-500/30'
              }`}>
                <div className="flex items-center gap-4 mb-3 sm:mb-0 flex-1">
                  <div className={`p-3 rounded-xl ${isUploaded ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                    {doc.id === 'video' ? <Video size={24} className={isUploaded ? 'text-emerald-500' : 'text-amber-500'} /> : 
                     <FileText className={isUploaded ? 'text-emerald-500' : 'text-amber-500'} size={24} />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{doc.name}</h3>
                    <p className="text-sm text-slate-400">{doc.description}</p>
                    {isUploaded && (
                      <div className="mt-2">
                        <p className="text-xs text-emerald-500 flex items-center gap-1">
                          <CheckCircle size={12} />
                          {isUploaded.name} ({(isUploaded.size / 1024).toFixed(1)} KB)
                        </p>
                        {isUploaded.type?.startsWith('video/') && (
                          <button
                            onClick={() => {
                              const win = window.open();
                              win.document.write(`
                                <html>
                                  <head><title>${isUploaded.name}</title></head>
                                  <body style="margin:0; background:#000; display:flex; align-items:center; justify-content:center; min-height:100vh;">
                                    <video src="${isUploaded.data}" controls style="max-width:100%; max-height:90vh;"></video>
                                  </body>
                                </html>
                              `);
                            }}
                            className="text-amber-500 text-xs mt-1 flex items-center gap-1 hover:underline"
                          >
                            <Eye size={12} /> Preview Video
                          </button>
                        )}
                      </div>
                    )}
                    {progress && progress < 100 && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-amber-500">Uploading...</span>
                          <span className="text-slate-400">{progress}%</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1">
                          <div className="bg-amber-500 h-1 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => handleFileUpload(doc.id)}
                  disabled={uploading || isUploaded}
                  className={`px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all min-w-[120px] ${
                    isUploaded 
                      ? 'bg-emerald-500/20 text-emerald-500 cursor-default' 
                      : uploading
                        ? 'bg-white/10 opacity-50 cursor-wait'
                        : 'bg-amber-500 text-slate-950 hover:bg-amber-400'
                  }`}
                >
                  {uploading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : isUploaded ? (
                    <Check size={18} />
                  ) : (
                    <Upload size={18} />
                  )}
                  {isUploaded ? 'Uploaded' : uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end mt-8">
          <button
            onClick={handleContinue}
            className={`px-8 py-4 rounded-xl font-bold flex items-center gap-3 transition-all ${
              Object.keys(uploadedDocs).length < requiredDocs.length
                ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                : 'bg-amber-500 text-slate-950 hover:bg-amber-400 hover:scale-105'
            }`}
            disabled={Object.keys(uploadedDocs).length < requiredDocs.length}
          >
            {t.documents?.continue || "Continue to Payment"}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
              
// ============= PAYMENT VIEW =============
function PaymentView({ t, user, navigate, showNotification, setApplicationStep }) {
  const [uploading, setUploading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('usdt');
  const [paymentProof, setPaymentProof] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  const paymentOptions = {
    usdt: {
      title: t.payment?.usdt || "USDT (TRC20)",
      address: 'TXq8P8x8x8x8x8x8x8x8x8x8x8x8x8x8x8x8x',
      instructions: t.payment?.instructions?.usdt || [
        'Send $15 USDT (TRC20 network only)',
        'Copy the address above',
        'Send from your wallet',
        'Take a screenshot of the transaction'
      ]
    },
    bank: {
      title: t.payment?.bank || "Bank Transfer (Ethiopia)",
      bankName: 'Commercial Bank of Ethiopia',
      accountName: 'GlobalTravel PLC',
      accountNumber: '1000123456789',
      branch: 'Bole Branch, Addis Ababa',
      instructions: t.payment?.instructions?.bank || [
        'Visit any Commercial Bank branch',
        'Deposit $15 USD (or equivalent ETB)',
        'Account Name: GlobalTravel PLC',
        'Account Number: 1000123456789',
        'Keep the deposit receipt'
      ]
    },
    telebirr: {
      title: t.payment?.telebirr || "TeleBirr / M-Pesa",
      number: '251911223344',
      name: 'GlobalTravel Official',
      instructions: t.payment?.instructions?.telebirr || [
        'Open TeleBirr or M-Pesa',
        'Send to: 251911223344',
        'Amount: $15 USD (or equivalent ETB)',
        'Reference: Your Full Name',
        'Take a screenshot of the confirmation'
      ]
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(t.payment?.copied || "Copied!");
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.style.display = 'none';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setUploading(true);
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64Data = reader.result;
        setPaymentProof(base64Data);
        setUploading(false);
        showNotification('Payment proof uploaded! Admin will review.', 'success');
        
        sendAdminEmail('payment_upload', {
          userName: user.email,
          userEmail: user.email,
          amount: 15,
          method: paymentMethod,
          paymentId: Date.now().toString()
        });
      };
    };
    
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  const handleSubmit = async () => {
    if (!paymentProof) {
      showNotification('Please upload payment proof first', 'error');
      return;
    }
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        payment: {
          method: paymentMethod,
          amount: 15,
          proof: paymentProof,
          status: 'pending',
          submittedAt: Timestamp.now()
        },
        applicationStep: 5,
        status: 'under-review',
        updatedAt: Timestamp.now()
      });
      
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      await sendAdminEmail('application_submitted', {
        userName: userData.name || user.email,
        userEmail: user.email,
        education: userData.education?.highestEducation || 'Not provided',
        documentsCount: Object.keys(userData.documents || {}).length,
        paymentStatus: 'pending'
      });
      
      setApplicationStep(5);
      showNotification('Application submitted successfully!', 'success');
      navigate('portal');
    } catch (error) {
      showNotification('Error submitting application', 'error');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">{t.payment?.title || "Application Fee: $15 USD"}</h1>
        <p className="text-slate-400">{t.payment?.step || "Step 4 of 4"}</p>
        <div className="w-full bg-slate-800 h-2 rounded-full mt-6">
          <div className="bg-amber-500 h-2 rounded-full" style={{ width: '100%' }}></div>
        </div>
      </div>

      <div className="bg-slate-900 border border-white/10 p-12 rounded-[3rem] shadow-3xl space-y-8">
        <div className="flex gap-4">
          <button
            onClick={() => setPaymentMethod('usdt')}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              paymentMethod === 'usdt' 
                ? 'border-amber-500 bg-amber-500/10' 
                : 'border-white/10 bg-white/5'
            }`}
          >
            <img src="https://cryptologos.cc/logos/tether-usdt-logo.png" alt="USDT" className="w-8 h-8 mx-auto mb-2" />
            <span className="text-sm font-bold">{paymentOptions.usdt.title}</span>
          </button>
          
          <button
            onClick={() => setPaymentMethod('bank')}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              paymentMethod === 'bank' 
                ? 'border-amber-500 bg-amber-500/10' 
                : 'border-white/10 bg-white/5'
            }`}
          >
            <Building size={24} className="mx-auto mb-2 text-amber-500" />
            <span className="text-sm font-bold">{paymentOptions.bank.title}</span>
          </button>
          
          <button
            onClick={() => setPaymentMethod('telebirr')}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              paymentMethod === 'telebirr' 
                ? 'border-amber-500 bg-amber-500/10' 
                : 'border-white/10 bg-white/5'
            }`}
          >
            <Smartphone size={24} className="mx-auto mb-2 text-amber-500" />
            <span className="text-sm font-bold">{paymentOptions.telebirr.title}</span>
          </button>
        </div>

        <div className="bg-white/5 p-8 rounded-2xl">
          {paymentMethod === 'usdt' && (
            <div>
              <h3 className="font-bold mb-4">{paymentOptions.usdt.title}</h3>
              <div className="bg-slate-950 p-4 rounded-lg mb-4">
                <p className="text-xs text-slate-400 mb-2">Wallet Address:</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm break-all">{paymentOptions.usdt.address}</code>
                  <button
                    onClick={() => handleCopy(paymentOptions.usdt.address)}
                    className="p-2 bg-white/5 rounded-lg hover:bg-white/10 shrink-0"
                  >
                    {copySuccess || (t.payment?.copy || "Copy")}
                  </button>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-400">
                {paymentOptions.usdt.instructions.map((instruction, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    {instruction}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {paymentMethod === 'bank' && (
            <div>
              <h3 className="font-bold mb-4">{paymentOptions.bank.title}</h3>
              <div className="space-y-3">
                <div className="bg-slate-950 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Bank:</p>
                  <p className="font-bold">{paymentOptions.bank.bankName}</p>
                </div>
                <div className="bg-slate-950 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Account Name:</p>
                  <p className="font-bold">{paymentOptions.bank.accountName}</p>
                </div>
                <div className="bg-slate-950 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Account Number:</p>
                  <p className="font-bold">{paymentOptions.bank.accountNumber}</p>
                </div>
                <div className="bg-slate-950 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Branch:</p>
                  <p className="font-bold">{paymentOptions.bank.branch}</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-slate-400 mt-4">
                {paymentOptions.bank.instructions.map((instruction, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    {instruction}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {paymentMethod === 'telebirr' && (
            <div>
              <h3 className="font-bold mb-4">{paymentOptions.telebirr.title}</h3>
              <div className="bg-slate-950 p-4 rounded-lg mb-4 text-center">
                <p className="text-xs text-slate-400 mb-2">Send to:</p>
                <p className="text-2xl font-bold text-amber-500">{paymentOptions.telebirr.number}</p>
                <p className="text-sm mt-2">{paymentOptions.telebirr.name}</p>
              </div>
              <ul className="space-y-2 text-sm text-slate-400">
                {paymentOptions.telebirr.instructions.map((instruction, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    {instruction}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="border-2 border-dashed border-white/10 rounded-2xl p-8 text-center">
          {paymentProof ? (
            <div>
              <CheckCircle size={40} className="text-emerald-500 mx-auto mb-4" />
              <p className="text-emerald-500 mb-2">Payment proof uploaded!</p>
              {paymentProof.startsWith('data:image') && (
                <img src={paymentProof} alt="Payment proof" className="max-w-md mx-auto rounded-lg border border-white/10 mt-4" />
              )}
            </div>
          ) : (
            <div>
              <Upload size={40} className="text-amber-500 mx-auto mb-4" />
              <p className="text-lg font-bold mb-2">{t.payment?.uploadProof || "Upload Payment Proof"}</p>
              <p className="text-sm text-slate-400 mb-4">
                After making payment, upload the screenshot or receipt
              </p>
              <button
                onClick={handleFileUpload}
                disabled={uploading}
                className="bg-amber-500 text-slate-950 px-8 py-4 rounded-xl font-black"
              >
                {uploading ? (t.payment?.uploading || "Uploading...") : (t.payment?.chooseFile || "Choose File")}
              </button>
            </div>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!paymentProof}
          className="w-full bg-amber-500 text-slate-950 py-5 rounded-2xl font-black disabled:opacity-50 flex items-center justify-center gap-3"
        >
          {t.payment?.complete || "Complete Application"}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ============= CONTACT VIEW =============
function ContactView({ t, showNotification }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    showNotification('Message sent! We\'ll reply within 24 hours.', 'success');
  };

  return (
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 py-20">
      <div className="space-y-12">
        <div className="space-y-6">
          <h2 className="text-7xl font-black tracking-tighter">{t.nav.contact}</h2>
          <p className="text-slate-400 font-medium text-lg leading-relaxed">{t.footer.desc}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-8">
          <div className="p-8 bg-slate-900 border border-white/5 rounded-[2rem] space-y-6 hover:border-amber-500/30 transition-all">
            <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-500"><Phone size={24}/></div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Voice Support</div>
              <div className="text-xl font-black">+251 11 667 8899</div>
            </div>
          </div>
          <div className="p-8 bg-slate-900 border border-white/5 rounded-[2rem] space-y-6 hover:border-amber-500/30 transition-all">
            <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-500"><Mail size={24}/></div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Direct Email</div>
              <div className="text-xl font-black">support@global.com</div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-slate-900 border border-white/10 p-12 rounded-[3rem] shadow-3xl space-y-8">
        <div className="space-y-4">
          <h3 className="text-3xl font-black tracking-tight">Direct Messaging</h3>
          <p className="text-slate-500 text-sm font-bold">Expect a response within 24 operational hours.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Subject</label>
            <input className="w-full bg-slate-950 border border-white/5 p-5 rounded-2xl outline-none focus:border-amber-500 transition-colors font-bold" placeholder="How can we help?" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Message</label>
            <textarea rows={5} className="w-full bg-slate-950 border border-white/5 p-5 rounded-2xl outline-none focus:border-amber-500 transition-colors font-bold resize-none" placeholder="Details..."></textarea>
          </div>
          <button type="submit" className="w-full bg-amber-500 text-slate-950 py-6 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-amber-500/20 flex items-center justify-center gap-3">
            <Send size={18}/> Send Message Now
          </button>
        </form>
      </div>
    </div>
  );
}

// ============= LOADING SCREEN =============
function LoadingScreen({ t }) {
  return (
    <div className="fixed inset-0 bg-slate-950 z-[1000] flex flex-col items-center justify-center gap-8">
      <div className="bg-amber-500 p-4 rounded-2xl animate-bounce text-slate-950 shadow-2xl shadow-amber-500/20">
        <Globe size={48} strokeWidth={3}/>
      </div>
      <div className="space-y-4 text-center">
        <div className="text-[11px] font-black uppercase tracking-[0.5em] text-amber-500 animate-pulse">{t.labels.loading}</div>
        <div className="w-48 h-1 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 animate-loading-bar" style={{ width: '40%' }}></div>
        </div>
      </div>
      <style>
        {`
          @keyframes loading-bar {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(250%); }
          }
          .animate-loading-bar {
            animation: loading-bar 2s infinite ease-in-out;
          }
        `}
      </style>
    </div>
  );
}

























